import { FitBox } from '../components/FitBox';
import { useNoticeToast } from '../lib/toast';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  api, fetchFineImageUrl, fetchFineScanImageUrl, uploadFineImage, type FineScanLine, type FineScanList, type FineScanRow, type FineScanRun, type BankCell, type FineCandidate, type FineLinkCandidate, type FineDeductExport, type FineImportResult, type FineNotifyPreview,
  type AcctCorp, type FineAllocHit, type FinePasteFormat, type FinePasteResult, type FineRow, type FineState, type FineDateBy,
} from '../lib/api';
import { ErrorBox, dash, money } from '../components/DataTable';
import Modal from '../components/Modal';
import { canScreen, useMe } from '../lib/me';
import { useAcctCorp, useSticky } from '../lib/sticky';
import { SortTh, useSort } from '../lib/sort';
import { useAutoSearch } from '../lib/search';

/**
 * 과태료·통행료 고지(#21) — 기획 `plan/accounting.md` 5-I. 서버 `functions/src/fine.ts`.
 *
 * 흐름: 고지서 입력 → **기사 자동 찾기**(위반일 당시 편성) → 납부일자 → 정산월별 **버스인 공제 엑셀** → 「공제 완료」.
 * 🔴 관리기사A(버스인 원문 `직영기사`)는 편성 기사가 실제 운전자가 아닐 수 있어 **영업담당자가 확인**한다
 *    (위반일 그 차의 배차 → 접수 담당자). 확인 전에는 공제 엑셀에서 빠진다.
 * 🔴 화면 낱말: 「차주」·「직영」·「지입」은 쓰지 않는다 → 기사님 · 관리기사A/B(저장값은 버스인 원문 그대로).
 */

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const kst = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
const addDays = (ymd: string, n: number) => new Date(Date.parse(`${ymd}T00:00:00Z`) + n * 86400_000).toISOString().slice(0, 10);
const KINDS = ['경찰청', '세외수입', '통행료', '과태료', '기타'];
/** 버스인 기사구분 원문 → 화면 라벨(dyops DRIVER_TYPE_LABELS 와 같다). 직영사업장기사는 어느 쪽도 아니다. */
const kindLabel = (k: string | null) => (k === '직영기사' ? '관리기사A' : k === '차주소유기사' ? '관리기사B' : null);

// 「전체」가 첫 탭·기본이다(#25) — 확인 필요는 관리기사A 만 담는 칸이라, 거기서 열면 관리기사B 가 없는 것처럼 보인다.
const STATES: [FineState, string][] = [
  ['all', '전체'], ['check', '확인 필요'], ['unpaid', '미납'], ['todeduct', '공제할 것'], ['deducted', '공제 완료'], ['skip', '공제 안 함'],
];

/** #169 기존 FNS 시트 색 — 검정 = 같은 고지서가 또 있음 · 빨강 = 마이너스 기사(차주 이월잔액 음수). 둘 다면 검정. */
const isMinus = (r: FineRow) => r.owner_remain != null && Number(r.owner_remain) < 0;
const rowFlagTitle = (r: FineRow) => [
  r.dup && '중복 — 같은 차량·위반일·금액 고지서가 또 있습니다',
  isMinus(r) && `마이너스 기사 — 차주 ${r.owner_name ?? ''} 이월잔액 ${Number(r.owner_remain).toLocaleString()}원`,
].filter(Boolean).join(' · ') || undefined;

function StateBadge({ r }: { r: FineRow }) {
  if (r.deduct_state === 'done') return <span className="rounded bg-emerald-50 px-1.5 text-xs text-emerald-700" title={`${dash(r.deduct_by)} · ${dash(r.deduct_at)}`}>공제 완료</span>;
  if (r.deduct_state === 'skip') return <span className="rounded bg-slate-100 px-1.5 text-xs text-ink-soft">공제 안 함</span>;
  if (r.match_state === 'check') return <span className="rounded bg-amber-50 px-1.5 text-xs text-amber-800" title={r.check_reason ?? ''}>확인 필요</span>;
  if (!r.paid_date) return <span className="rounded bg-red-50 px-1.5 text-xs text-red-700">미납</span>;
  return <span className="rounded bg-sky-50 px-1.5 text-xs text-sky-700">공제할 것</span>;
}

function DriverCell({ r }: { r: FineRow }) {
  const label = kindLabel(r.driver_kind);
  return (
    <span className="whitespace-nowrap">
      {dash(r.driver_name)}
      {label && <span className={`ml-1 rounded px-1 text-[10px] ${label === '관리기사A' ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-ink-soft'}`}>{label}</span>}
      {r.match_state === 'confirmed' && <span className="ml-1 rounded bg-emerald-50 px-1 text-[10px] text-emerald-700" title={`${dash(r.confirmed_by)} · ${dash(r.confirmed_at)}`}>확인됨</span>}
      {r.match_source === 'live' && <span className="ml-1 text-[10px] text-ink-faint" title="웹 편성에 없어 버스인 실시간 편성에서 찾았습니다">실시간</span>}
    </span>
  );
}

/** 고지서 사진 — 올린 사진(비공개, 로그인해서 본다)이 먼저, 없으면 시트에서 옮긴 드라이브 링크. */
function PhotoLink({ r, label = '보기' }: { r: FineRow; label?: string }) {
  const [err, setErr] = useState<string | null>(null);
  if (r.has_photo) {
    return (
      <button className="text-brand-700 underline" title={r.photo_name ?? ''}
              onClick={(e) => {
                e.stopPropagation();
                // 창을 먼저 연다 — await 뒤에 열면 팝업 차단에 걸린다
                const w = window.open('', '_blank');
                fetchFineImageUrl(r.fine_id).then((u) => { if (w) w.location.href = u; else window.location.href = u; })
                  .catch((x) => { w?.close(); setErr(errMsg(x)); });
              }}>
        {label}{err && <span className="ml-1 text-xs text-red-600">{err}</span>}
      </button>
    );
  }
  if (r.photo_url) return <a href={r.photo_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-brand-700 underline">{label}</a>;
  return <>—</>;
}

function NotifyBadge({ r }: { r: FineRow }) {
  const n = r.last_notify;
  if (!n) return <span className="text-ink-faint">—</span>;
  return n.status === 'sent'
    ? <span className="whitespace-nowrap text-xs text-emerald-700" title={`${dash(n.to)} · ${n.at}`}>보냄 {n.at.slice(5, 10)}</span>
    : <span className="whitespace-nowrap text-xs text-red-700" title={n.error ?? ''}>실패</span>;
}

/** 칸별 거르기 칸의 도움말(#191). */
const COL_HINT: Record<string, string> = {
  violation: '예) 2026-08 · 2026-08-16', kind: '경찰청 · 세외수입 · 통행료', car_no: '번호 일부(띄어쓰기 무시)', driver: '기사·차주·소속·기사구분(관리A=직영기사)',
  place: '장소·내용·발행기관', amount: '숫자만(쉼표 가능)', due_date: '예) 2026-10', paid_date: '예) 2026-10-06', deduct_mon: '예) 2026-10', created: '예) 2026-10-12',
};

export function FineTab({ corps }: { corps: AcctCorp[] }) {
  // 법인(#71) — 회계 화면 공용 값(입금 내역·출납일보와 같이 기억된다).
  const [corpId, setCorpId] = useAcctCorp();
  const [state, setState] = useSticky<FineState>('acct.fine.state', 'all');
  const [from, setFrom] = useSticky('acct.fine.from', addDays(kst(), -365));
  const [to, setTo] = useSticky('acct.fine.to', kst());
  // #129 — 기간을 무엇으로 거를지(위반일 · 납부기한 · 납부일자). 「미납」 탭과 겹치면 「이 기간 납부기한인데 안 낸 것」.
  const [dateBy, setDateBy] = useSticky<FineDateBy>('acct.fine.dateBy', 'violation');
  const [kind, setKind] = useSticky('acct.fine.kind', '');
  const [term, setTerm] = useSticky('acct.fine.term', '');
  const [q, setQ] = useSticky('acct.fine.q', '');
  const [page, setPage] = useState(0);
  // 칸별 거르기(#191) — 칸 아래 칸에 친 대로(서버가 거른다 · fine.ts FINE_COL_FILTER).
  const [colDraft, setColDraft] = useState<Record<string, string>>({});
  const [cols, setCols] = useState<Record<string, string>>({});
  useAutoSearch(JSON.stringify(colDraft), () => { setCols(colDraft); setPage(0); });
  const [nonce, setNonce] = useState(0);
  const [data, setData] = useState<Awaited<ReturnType<typeof api.listFines>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FineRow | 'new' | null>(null);
  const [panel, setPanel] = useState<'import' | 'deduct' | 'paste' | 'notify' | 'photos' | 'scan' | 'paid' | 'auto' | 'wehago' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const PAGE = 200;
  // 머리글 정렬(#37) — 200건씩 받으니 서버가 정렬한다(fine.ts FINE_SORT). 정렬을 바꾸면 1쪽부터.
  const [sort, toggleSort] = useSort('acct.fine');
  const onSort = (k: string) => { toggleSort(k); setPage(0); };
  useEffect(() => { setPicked(new Set()); }, [state, from, to, dateBy, kind, q, page, corpId, cols]);
  const toggle = (id: number) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.listFines({ state, from: from || undefined, to: to || undefined, dateBy, kind: kind || undefined, q: q || undefined, corpId, limit: PAGE, offset: page * PAGE, sort: sort?.key, dir: sort?.dir, filters: cols })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [state, from, to, dateBy, kind, q, page, nonce, sort, corpId, cols]);
  const reload = () => setNonce((n) => n + 1);
  // 스캔함 배지 — 등록 대기·확인 필요·오류 건수(스캔함 서버가 없을 때는 조용히 0)
  const [scanTodo, setScanTodo] = useState(0);
  useEffect(() => {
    let off = false;
    api.listFineScans({ state: 'todo', limit: 1 }).then((r) => { if (!off) setScanTodo(r.todo); }).catch(() => {});
    return () => { off = true; };
  }, [nonce, panel]);

  async function rematch() {
    setNotice('다시 찾는 중…');
    try {
      const r = await api.rematchFines({});
      setNotice(`확인 필요 ${r.targets}건을 다시 찾았습니다 — 찾음 ${r.match.ok} · 여전히 확인 필요 ${r.match.check}${r.match.live === 'failed' ? ' · ⚠️ 버스인 실시간 편성을 못 읽었습니다' : ''}`);
      reload();
    } catch (e) { setNotice(errMsg(e)); }
  }

  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE));
  const body = useMemo(() => (data?.rows ?? []).map((r) => (
    <tr key={r.fine_id} onDoubleClick={() => setEditing(r)} title={rowFlagTitle(r) ?? '두 번 누르면 고지서 창'}
        className={`cursor-pointer border-b ${r.dup ? 'fine-dup' : isMinus(r) ? 'fine-minus' : 'hover:bg-brand-50/40'}`}>
      <td className="px-2" data-nocopy="1" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={picked.has(r.fine_id)} onChange={() => toggle(r.fine_id)} aria-label="고르기(알림톡·납부일자 일괄)" />
      </td>
      <td className="whitespace-nowrap px-2 py-1.5"><StateBadge r={r} /></td>
      <td className="whitespace-nowrap px-2">{r.violation_date}{r.violation_time && <span className="ml-1 text-ink-faint">{r.violation_time}</span>}</td>
      <td className="px-2">{r.kind}</td>
      <td className="whitespace-nowrap px-2 font-medium">{r.car_no}</td>
      <td className="px-2"><DriverCell r={r} /></td>
      <td className="max-w-[16rem] truncate px-2" title={[r.place, r.content].filter(Boolean).join(' / ')}>{[r.place, r.content].filter(Boolean).join(' / ') || '—'}</td>
      <td className="px-2 text-right">{money(r.amount)}</td>
      <td className="whitespace-nowrap px-2">{dash(r.due_date)}</td>
      <td className="whitespace-nowrap px-2">{dash(r.paid_date)}</td>
      <td className="whitespace-nowrap px-2">{dash(r.eff_deduct_mon)}</td>
      <td className="whitespace-nowrap px-2 text-xs text-ink-soft">{dash(r.created_at)}</td>
      <td className="max-w-[14rem] truncate px-2 text-xs text-ink-soft" title={r.check_reason ?? ''}>
        {r.match_state === 'check' ? r.check_reason : ''}{r.sales_name && <span className="ml-1 text-violet-700">→ {r.sales_name}</span>}
      </td>
      <td className="whitespace-nowrap px-2"><PhotoLink r={r} /></td>
      <td className="whitespace-nowrap px-2"><NotifyBadge r={r} /></td>
    </tr>
  )), [data, picked]);
  const pageIds = (data?.rows ?? []).map((r) => r.fine_id);
  const allPicked = pageIds.length > 0 && pageIds.every((id) => picked.has(id));

  return (
    <div className="space-y-3">
      <div className="card space-y-1.5 px-3 py-2">
        {/* 설명은 한 줄로(10-16 way 「상단 공간 최대 줄여 아래 화면 위주로」) — 마우스를 올리면 펼친다. */}
        <p className="page-hint">
          고지서를 넣으면 <b>위반일에 그 차를 몰던 기사</b>를 편성에서 찾습니다. <b>관리기사A</b> 차량은 그날 배차의 <b>버스인접수담당자</b>에게 실제 기사 확인을 요청합니다.
          납부일자를 넣으면 그 달(바꿀 수 있습니다) 「공제할 것」이 되고, <b>공제</b>에서 버스인 업로드 엑셀을 받거나 — 버스인웹 전환 뒤에는 — <b>재무추가에 바로 공제</b>합니다.
        </p>
        {/* 상태 탭 + 조건을 한 줄에 — 칸 이름은 칸 왼쪽에 작게(위에 따로 줄을 두지 않는다). */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <div className="flex flex-wrap gap-0.5">
            {STATES.map(([id, label]) => {
              const n = id === 'check' ? data?.badge.check : id === 'todeduct' ? data?.badge.todeduct : id === 'unpaid' ? data?.badge.unpaid : undefined;
              return (
                <button key={id} onClick={() => { setState(id); setPage(0); }}
                        className={`rounded-lg px-2.5 py-1 text-sm ${state === id ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink-soft hover:bg-canvas'}`}>
                  {label}{n !== undefined && n > 0 && <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-xs text-amber-800">{n.toLocaleString()}</span>}
                </button>
              );
            })}
          </div>
          <span className="h-5 w-px bg-line" aria-hidden />
          <label className="flex items-center gap-1"><span className="text-xs text-ink-soft">법인</span>
            <select className="field w-32 py-1" value={corpId ?? ''} onChange={(e) => { setCorpId(e.target.value ? Number(e.target.value) : null); setPage(0); }}
                    title="고지서의 법인 칸 기준입니다. 법인이 비었거나 다른 이름(더모아투어·직원 등)인 고지서는 「전체 법인」에서만 보입니다.">
              <option value="">전체 법인</option>
              {corps.map((c) => (
                <option key={c.corp_id} value={c.corp_id}>{c.parent_corp_id ? `└ ${c.short_name}` : c.short_name}</option>
              ))}
            </select></label>
          <label className="flex items-center gap-1"><span className="text-xs text-ink-soft">기간</span>
            <select className="field w-auto py-1" value={dateBy} onChange={(e) => { setDateBy(e.target.value as FineDateBy); setPage(0); }}
                    title="시작·끝 날짜를 어느 날짜로 거를지 — 납부기한 + 「미납」 탭 = 그 기간 납부기한인데 아직 안 낸 것">
              <option value="violation">위반일</option><option value="due">납부기한</option><option value="paid">납부일자</option><option value="created">업로드 일시</option>
            </select></label>
          <span className="flex items-center gap-1">
            <input type="date" className="field w-auto py-1" aria-label={`${dateBy === 'due' ? '납부기한' : dateBy === 'paid' ? '납부일자' : dateBy === 'created' ? '업로드일' : '위반일'} 시작`} value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} />
            <span className="text-ink-faint">~</span>
            <input type="date" className="field w-auto py-1" aria-label="끝" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} />
          </span>
          <label className="flex items-center gap-1"><span className="text-xs text-ink-soft">구분</span>
            <select className="field w-auto py-1" value={kind} onChange={(e) => { setKind(e.target.value); setPage(0); }}>
              <option value="">전체</option>{KINDS.map((k) => <option key={k}>{k}</option>)}
            </select></label>
          <form className="flex items-stretch gap-1" onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setPage(0); }}>
            <input className="field w-52 py-1" placeholder="차량·기사·장소·법인 검색" aria-label="검색(차량·기사·장소·법인)" value={term} onChange={(e) => setTerm(e.target.value)} />
            <button className="btn-search" aria-label="찾기" title="찾기" />
          </form>
        </div>
        {/* 할 일 버튼 — 작게 한 줄(넘치면 다음 줄). */}
        <div className="btn-row-sm flex flex-wrap gap-1">
          <button className="btn-primary" onClick={() => setPanel('scan')} title="드라이브에 올린 고지서 스캔 — AI 가 읽어 두면 확인 후 등록">스캔함{scanTodo > 0 && <span className="ml-1 rounded-full bg-white/90 px-1.5 text-xs text-brand-700">{scanTodo}</span>}</button>
          <button className="btn-primary" onClick={() => setPanel('paste')} title="이파인(경찰청)·위택스(세외수입) 조회표나 통행료 표를 복사해 붙여 넣습니다">붙여넣기 입력</button>
          <button className="btn-ghost" onClick={() => setEditing('new')}>+ 한 건 입력</button>
          <button className="btn-ghost" disabled={picked.size === 0} onClick={() => setPanel('notify')}
                  title="고른 고지서를 기사님께 알림톡으로 알립니다(보내기 전에 받는 사람을 먼저 보여 줍니다)">
            알림톡{picked.size > 0 && ` (${picked.size})`}
          </button>
          <button className="btn-ghost" onClick={() => setPanel('auto')}
                  title="매일 17시에 그날까지 등록된 고지서를 기사님께 알림톡으로 자동 발송합니다">자동 알림톡(17시)</button>
          <button className="btn-ghost" disabled={picked.size === 0} onClick={() => setPanel('paid')}
                  title="고른 고지서에 같은 납부일자를 한 번에 넣습니다">
            납부일자 일괄{picked.size > 0 && ` (${picked.size})`}
          </button>
          <button className="btn-ghost" onClick={() => setPanel('deduct')}>공제</button>
          <button className="btn-ghost" onClick={() => setPanel('wehago')}
                  title="납부일을 고르면 그날 낸 고지서의 적요를 위하고 일반전표 대량 업로드 엑셀로 내려받습니다">위하고 전표 엑셀</button>
          <button className="btn-ghost" onClick={() => void rematch()} title="확인 필요 줄의 기사를 편성에서 다시 찾습니다(영업·회계가 확인한 줄은 그대로)">다시 찾기</button>
          <button className="btn-ghost" onClick={() => setPanel('photos')}
                  title="고지서 사진을 여러 장 한 번에 골라 각 고지서에 붙입니다(체크한 고지서가 있으면 그 안에서 짝짓습니다)">고지서 사진 여러 장</button>
        </div>
      </div>

      {error && <ErrorBox message={error} />}
      <FitBox deps={[data, loading]}>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-ink-soft">
            <tr>
              <th className="px-2">
                <input type="checkbox" checked={allPicked} aria-label="이 쪽 전부 고르기"
                       onChange={() => setPicked((s) => { const n = new Set(s); pageIds.forEach((id) => (allPicked ? n.delete(id) : n.add(id))); return n; })} />
              </th>
              <SortTh className="px-2 py-2" sortKey="deduct_state" sort={sort} onSort={onSort}>상태</SortTh>
              <SortTh className="px-2" sortKey="violation_date" sort={sort} onSort={onSort}>위반일시</SortTh>
              <SortTh className="px-2" sortKey="kind" sort={sort} onSort={onSort}>구분</SortTh>
              <SortTh className="px-2" sortKey="car_no" sort={sort} onSort={onSort}>차량번호</SortTh>
              <SortTh className="px-2" sortKey="driver_name" sort={sort} onSort={onSort}>기사</SortTh>
              <SortTh className="px-2" sortKey="place" sort={sort} onSort={onSort}>장소 / 내용</SortTh>
              <SortTh className="px-2 text-right" sortKey="amount" sort={sort} onSort={onSort}>금액</SortTh>
              <SortTh className="px-2" sortKey="due_date" sort={sort} onSort={onSort}>납부기한</SortTh>
              <SortTh className="px-2" sortKey="paid_date" sort={sort} onSort={onSort}>납부일자</SortTh>
              <SortTh className="px-2" sortKey="deduct_mon" sort={sort} onSort={onSort}>공제월</SortTh>
              <SortTh className="px-2" sortKey="created_at" sort={sort} onSort={onSort}>업로드 일시</SortTh>
              <SortTh className="px-2" sortKey="match_state" sort={sort} onSort={onSort}>확인</SortTh>
              <th className="px-2">고지서</th><th className="px-2">알림톡</th>
            </tr>
            <tr>
              <th /><th />
              {(['violation', 'kind', 'car_no', 'driver', 'place', 'amount', 'due_date', 'paid_date', 'deduct_mon', 'created'] as const).map((k) => (
                <th key={k} className="px-1 pb-1 font-normal">
                  <input className="field w-full min-w-[4rem] px-1.5 py-0.5 text-xs" placeholder="검색" value={colDraft[k] ?? ''}
                         aria-label={`${k} 칸 검색`} title={COL_HINT[k]}
                         onChange={(e) => setColDraft((c) => ({ ...c, [k]: e.target.value }))} />
                </th>
              ))}
              <th /><th /><th />
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
        {!loading && data && data.rows.length === 0 && <div className="p-6 text-center text-sm text-ink-soft">조건에 맞는 고지서가 없습니다.</div>}
        {loading && <div className="p-4 text-sm text-ink-soft">불러오는 중…</div>}
      </FitBox>
      {data && (
        <div className="flex items-center justify-between text-sm text-ink-soft">
          <span>{data.total.toLocaleString()}건 · 합계 {money(data.amount)}원
            <span className="ml-3 text-xs"><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-gray-900 align-middle" />중복 고지서
              <span className="ml-2 mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-red-600 align-middle" />마이너스 기사(차주 이월잔액 마이너스)</span>
          </span>
          {pages > 1 && (
            <span className="flex items-center gap-2">
              <button className="btn-ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>이전</button>
              {page + 1} / {pages}
              <button className="btn-ghost" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>다음</button>
            </span>
          )}
        </div>
      )}

      {editing && (
        <Modal width="max-w-4xl">
          <FineEdit row={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }}
                    onRefresh={(r) => { reload(); void refetchFine(r).then((x) => { if (x) setEditing((cur) => (cur && cur !== 'new' && cur.fine_id === x.fine_id ? x : cur)); }); }} />
        </Modal>
      )}
      {panel === 'import' && (
        <Modal>
          <FineImport onClose={() => setPanel(null)} onDone={reload} />
        </Modal>
      )}
      {panel === 'deduct' && (
        <Modal>
          <DeductPanel onClose={() => setPanel(null)} onDone={reload} />
        </Modal>
      )}
      {panel === 'paste' && (
        <Modal width="max-w-5xl">
          <FinePaste onClose={() => setPanel(null)} onDone={reload} />
        </Modal>
      )}
      {panel === 'scan' && (
        <Modal width="max-w-6xl">
          <FineScanPanel onClose={() => setPanel(null)} onDone={reload} />
        </Modal>
      )}
      {panel === 'photos' && (
        <Modal width="max-w-5xl">
          <FinePhotoBulk pool={(data?.rows ?? []).filter((r) => picked.size === 0 || picked.has(r.fine_id))}
                         onClose={() => setPanel(null)} onDone={reload} />
        </Modal>
      )}
      {panel === 'paid' && (
        <Modal width="max-w-md">
          <PaidDateBulk fineIds={[...picked]} onClose={() => setPanel(null)} onDone={(msg) => { setPanel(null); setNotice(msg); setPicked(new Set()); reload(); }} />
        </Modal>
      )}
      {panel === 'wehago' && (
        <Modal width="max-w-3xl">
          <WehagoExport corpId={corpId} onClose={() => setPanel(null)} />
        </Modal>
      )}
      {panel === 'auto' && (
        <Modal width="max-w-3xl">
          <FineAutoNotify onClose={() => setPanel(null)} />
        </Modal>
      )}
      {panel === 'notify' && (
        <Modal width="max-w-4xl">
          <FineNotify fineIds={[...picked]} onClose={() => setPanel(null)} onDone={() => { setPicked(new Set()); reload(); }} />
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 고지서 입력·수정
// ─────────────────────────────────────────────────────────────────────────────
/**
 * 위하고 일반전표 대량 업로드 엑셀(#169) — 납부일(기간)을 고르면 그날 낸 고지서마다 한 줄.
 * 칸 = 위하고 템플릿 머리글 그대로(1.월 ~ 10.대변) · 월·일 = 납부일 · 8.적요명 = 기존 시트 「BUSIN」 칸 · 9.차변 = 금액.
 * 7.거래처명 = 고지서 구분(경찰청·세외수입·통행료 · #458) · 구분·계정·거래처코드는 회사마다 달라 비워 둔다(위하고에서 채운다).
 */
const WEHAGO_HEAD = ['1.월', '2.일', '3.구분', '4.계정과목코드', '5.계정과목명', '6.거래처코드', '7.거래처명', '8.적요명', '9.차변(입금)', '10.대변(출금)'];
/** 위하고 적요는 58자까지(공백 포함 · #169 유상은 10-12 — 30자는 뒤가 너무 잘려 58로) — 엑셀에만 자른다(미리보기는 전체). 글자 단위(한글·이모지 쪼개짐 없음). */
const wehagoSummary = (s: string) => Array.from(s).slice(0, 58).join('');
function WehagoExport({ corpId, onClose }: { corpId: number | null; onClose: () => void }) {
  const [from, setFrom] = useState(kst());
  const [to, setTo] = useState(kst());
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.exportFineWehago>>['rows'] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!from) return;
    let off = false;
    setBusy(true); setError(null);
    api.exportFineWehago({ from, to: to || from, corpId })
      .then((r) => { if (!off) setRows(r.rows); })
      .catch((e) => { if (!off) { setError(errMsg(e)); setRows(null); } })
      .finally(() => { if (!off) setBusy(false); });
    return () => { off = true; };
  }, [from, to, corpId]);
  const total = (rows ?? []).reduce((a, r) => a + Number(r.amount), 0);
  async function download() {
    if (!rows?.length) return;
    const XLSX = await import('xlsx');
    const aoa: (string | number)[][] = [WEHAGO_HEAD, ...rows.map((r) => [
      Number(r.paidDate.slice(5, 7)), Number(r.paidDate.slice(8, 10)), '', '', '', '', r.kind ?? '', wehagoSummary(r.summary), Math.round(Number(r.amount)), '',
    ])];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [8, 8, 9.6, 13.5, 13.5, 13.75, 19, 75.75, 13.1, 13.1].map((wch) => ({ wch }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `위하고_일반전표_과태료_${from}${to && to !== from ? `_${to}` : ''}.xlsx`);
  }
  return (
    <div className="card space-y-3 p-5">
      <h3 className="text-base font-semibold">위하고 전표 엑셀</h3>
      <p className="text-sm text-ink-soft">
        납부일을 고르면 그날 낸 고지서의 적요가 들어간 <b>위하고 일반전표 대량 업로드</b> 엑셀을 내려받습니다.
        월·일·거래처명(경찰청·세외수입·통행료)·적요명·차변 금액이 채워지고, 구분·계정과목·거래처코드는 위하고에서 넣으시면 됩니다.{corpId !== null && ' (지금 고른 법인 것만)'}
      </p>
      <div className="flex flex-wrap items-end gap-2 text-sm">
        <label className="flex flex-col"><span className="text-xs text-ink-soft">납부일</span>
          <input type="date" className="field w-44" value={from} onChange={(e) => { setFrom(e.target.value); if (!to || to < e.target.value) setTo(e.target.value); }} /></label>
        <label className="flex flex-col"><span className="text-xs text-ink-soft">끝(여러 날이면)</span>
          <input type="date" className="field w-44" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <span className="ml-auto text-ink-soft">{busy ? '불러오는 중…' : rows ? `${rows.length.toLocaleString()}건 · 합계 ${total.toLocaleString()}원` : ''}</span>
      </div>
      {error && <ErrorBox message={error} />}
      {rows && rows.length > 0 && (
        <div className="max-h-[50vh] overflow-auto rounded border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-canvas text-left text-ink-soft"><tr><th className="px-2 py-1">납부일</th><th className="px-2">적요명</th><th className="px-2 text-right">금액</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.fineId} className="border-t"><td className="whitespace-nowrap px-2 py-1">{r.paidDate}</td><td className="px-2">{r.summary}</td><td className="px-2 text-right">{money(r.amount)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {rows && rows.length === 0 && !busy && <div className="text-sm text-ink-soft">그 납부일에 낸 고지서가 없습니다.</div>}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>닫기</button>
        <button className="btn-primary" onClick={() => void download()} disabled={busy || !rows?.length}>엑셀 내려받기</button>
      </div>
    </div>
  );
}

/** 납부일자 일괄(#129) — 체크한 고지서에 같은 납부일자. 공제를 마쳤거나 공제 안 함인 줄은 서버가 건너뛴다. */
function PaidDateBulk({ fineIds, onClose, onDone }: { fineIds: number[]; onClose: () => void; onDone: (msg: string) => void }) {
  const [paid, setPaid] = useState(kst());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function go() {
    setBusy(true); setError(null);
    try {
      const r = await api.setFinePaidDate({ fineIds, paidDate: paid });
      onDone(`${r.updated}건에 납부일자 ${paid} 를 넣었습니다`
        + (r.skipped ? ` · 공제를 마쳤거나 공제 안 함인 ${r.skipped}건은 그대로 두었습니다` : '')
        + (r.deduct.created ? ` · 재무추가 공제 ${r.deduct.created}건` : ''));
    } catch (e) { setError(errMsg(e)); setBusy(false); }
  }
  return (
    <div className="card space-y-3 p-5">
      <h3 className="text-base font-semibold">납부일자 일괄 입력</h3>
      <p className="text-sm text-ink-soft">고른 고지서 {fineIds.length}건에 같은 납부일자를 넣습니다.</p>
      <label className="flex flex-col text-sm"><span className="text-xs text-ink-soft">납부일자</span>
        <input type="date" className="field w-44" value={paid} onChange={(e) => setPaid(e.target.value)} /></label>
      {error && <ErrorBox message={error} />}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>닫기</button>
        <button className="btn-primary" onClick={() => void go()} disabled={busy || !paid}>{busy ? '넣는 중…' : '넣기'}</button>
      </div>
    </div>
  );
}

/** 창을 연 채 그 고지서 한 줄만 다시 읽는다(#464) — 목록 탭·쪽과 무관하게 차량번호·위반일로 찾는다. */
async function refetchFine(r: FineRow): Promise<FineRow | null> {
  const d = r.violation_date;
  const res = await api.listFines({ state: 'all', dateBy: 'violation', from: d, to: d, q: r.car_no, limit: 200 }).catch(() => null);
  return res?.rows.find((x) => x.fine_id === r.fine_id) ?? null;
}

function FineEdit({ row, onClose, onSaved, onRefresh }: { row: FineRow | null; onClose: () => void; onSaved: () => void; onRefresh?: (r: FineRow) => void }) {
  const [f, setF] = useState(() => ({
    kind: row?.kind ?? '경찰청', corpName: row?.corp_name ?? '', issuer: row?.issuer ?? '', carNo: row?.car_no ?? '',
    violationDate: row?.violation_date ?? '', violationTime: row?.violation_time ?? '', place: row?.place ?? '', content: row?.content ?? '',
    noticeNo: row?.notice_no ?? '', giroNo: row?.giro_no ?? '', dueDate: row?.due_date ?? '', amount: row ? String(Math.round(Number(row.amount))) : '',
    photoUrl: row?.photo_url ?? '', memo: row?.memo ?? '', paidDate: row?.paid_date ?? '', deductMon: row?.deduct_mon ?? '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = row?.deduct_state === 'done';
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function save() {
    setBusy(true); setError(null);
    try {
      const r = await api.saveFine({ fineId: row?.fine_id, ...f, amount: f.amount.replace(/,/g, '') });
      // 재무추가에 못 들어갔으면 창을 닫지 않고 이유를 보인다(출납일보와 같다)
      if (r.deduct?.pending.length) { setError(`저장했습니다. 재무추가는 대기 — ${r.deduct.pending.join(' · ')}`); return; }
      onSaved();
    }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  async function del() {
    if (!row) return;
    setBusy(true); setError(null);
    try { await api.deleteFine({ fineId: row.fine_id }); onSaved(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  async function setDeduct(state: 'none' | 'skip' | 'done') {
    if (!row) return;
    setBusy(true); setError(null);
    try {
      const r = await api.setFineDeductState({ fineIds: [row.fine_id], state });
      if (r.updated === 0) throw new Error('바꾸지 못했습니다 — 공제 완료는 납부일자가 있고 기사가 정해진 고지서만 표시할 수 있습니다.');
      onSaved();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  const input = (k: keyof typeof f, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="flex flex-col text-sm"><span className="text-xs text-ink-soft">{label}</span>
      <input className="field" value={f[k]} onChange={set(k)} {...props} /></label>
  );

  return (
    <>
      <div className="card space-y-4 p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-bold">{row ? '고지서' : '고지서 입력'}</h2>
          {row && <StateBadge r={row} />}
        </div>
        {done && <div className="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{row?.result_daily_id && row.rd_linked ? `출납일보·재무추가에서 이미 공제한 줄(재무추가 NO ${row.result_daily_id})에 연결된 고지서입니다. 연결을 풀어도 그 공제 줄은 그대로 남습니다.` : row?.result_daily_id ? `재무추가 NO ${row.result_daily_id} 로 공제한 고지서입니다. 금액·차량·날짜를 고쳐 저장하면 재무추가 줄도 새 값으로 바뀌고, 삭제하면 같이 지워집니다.` : '버스인 공제를 마친 고지서입니다. 차량번호·위반일자는 바꿀 수 없습니다(공제한 기사가 바뀝니다).'}</div>}
        {row && row.deduct_state === 'none' && row.paid_date && <AlreadyDeducted row={row} onDone={onSaved} />}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <label className="flex flex-col text-sm"><span className="text-xs text-ink-soft">구분</span>
            <select className="field" value={f.kind} onChange={set('kind')}>{KINDS.map((k) => <option key={k}>{k}</option>)}</select></label>
          {input('carNo', '차량번호 *', { disabled: done })}
          {input('violationDate', '위반일자 *', { type: 'date', disabled: done })}
          {input('violationTime', '위반시간', { placeholder: '13:45' })}
          {input('amount', '금액 *', { inputMode: 'numeric' })}
          {input('dueDate', '납부기한', { type: 'date' })}
          {input('paidDate', '납부일자', { type: 'date' })}
          {input('deductMon', '공제 정산월(비면 납부일 기준 · 1~5일 납부는 전월)', { type: 'month' })}
          {input('corpName', '법인')}
          {input('issuer', '발행기관')}
          {input('noticeNo', '고지번호')}
          {input('giroNo', '지로번호')}
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {input('place', '위반장소')}
          {input('content', '위반내용')}
          {input('photoUrl', '고지서 사진 링크(드라이브)')}
          {input('memo', '비고')}
        </div>
        {row && <PhotoUpload row={row} onDone={onSaved} />}

        {row && (
          <div className="rounded border p-3 text-sm">
            <div className="mb-1 font-medium">기사</div>
            <div className="flex flex-wrap items-center gap-2">
              <DriverCell r={row} />
              {row.grp_name && <span className="text-ink-soft">· {row.grp_name}</span>}
              {row.car_alloc_id && <span className="text-xs text-ink-faint">편성 {row.car_alloc_id}</span>}
            </div>
            {row.match_state === 'check' && (
              <div className="mt-2 rounded bg-amber-50 px-3 py-2 text-amber-900">
                {row.check_reason}{row.sales_name && <> — 확인 요청: <b>{row.sales_name}</b></>}
                {row.sales_note && <div className="mt-1 text-xs">그날 배차: {row.sales_note}</div>}
              </div>
            )}
            {(row.deduct_state === 'none' || (row.deduct_state === 'done' && row.result_daily_id)) && <ConfirmPicker row={row} onDone={onRefresh ? () => onRefresh(row) : onSaved} acct />}
          </div>
        )}

        {error && <ErrorBox message={error} />}
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex gap-2">
            {row && (!done || row.result_daily_id) && <button className="btn-ghost text-red-600" disabled={busy} onClick={() => void del()}>삭제</button>}
            {row && row.deduct_state === 'none' && <button className="btn-ghost" disabled={busy} onClick={() => void setDeduct('skip')} title="회사 부담·기사 직접 납부 등 공제하지 않을 때">공제 안 함</button>}
            {row && row.deduct_state !== 'none' && (
              <button className="btn-ghost" disabled={busy} onClick={() => void setDeduct('none')}
                title={row.result_daily_id && !row.rd_linked ? `재무추가 공제 줄(#${row.result_daily_id})도 함께 지웁니다` : undefined}>
                {row.result_daily_id && row.rd_linked ? '연결 풀기' : row.result_daily_id ? '공제 되돌리기(재무추가 줄 지움)' : '공제 표시 되돌리기'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={onClose} disabled={busy}>취소</button>
            <button className="btn-primary" onClick={() => void save()} disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
          </div>
        </div>
      </div>
    </>
  );
}

/** 고지서 사진 올리기 — 저장 버튼과 따로, 고르면 바로 올라가 이 고지서에 붙는다. */
function PhotoUpload({ row, onDone }: { row: FineRow; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setError(null); setDone(null);
    try { const r = await uploadFineImage(row.fine_id, file); setDone(r.name); } catch (x) { setError(errMsg(x)); } finally { setBusy(false); }
  }
  return (
    <div className="flex flex-wrap items-center gap-3 rounded border p-3 text-sm">
      <span className="font-medium">고지서 사진</span>
      {row.has_photo || done ? <span className="text-ink-soft">{done ?? row.photo_name ?? '올림'}{!done && <> · <PhotoLink r={row} /></>}</span>
        : row.photo_url ? <span className="text-ink-soft">드라이브 링크 · <PhotoLink r={row} /></span> : <span className="text-ink-faint">없음</span>}
      <label className="btn-ghost cursor-pointer">
        {busy ? '올리는 중…' : row.has_photo || done ? '바꾸기' : '올리기'}
        <input type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" disabled={busy} onChange={(e) => void pick(e)} />
      </label>
      <span className="text-xs text-ink-faint">JPG·PNG·PDF · 기사님이 알림톡 「고지서 보기」로 이 사진을 봅니다</span>
      {done && <button className="btn-ghost text-xs" onClick={onDone}>목록 새로고침</button>}
      {error && <ErrorBox message={error} />}
    </div>
  );
}

/**
 * 고지서 사진 여러 장(#79) — 파일을 한꺼번에 고르면 **파일 이름**으로 고지서를 짝지어 두고, 못 찾은 것만 사람이 고른다.
 * 짝짓기 대상 = 지금 목록(체크한 줄이 있으면 그 줄만). 올리기는 한 장씩 기존 `uploadFineImage` — 서버 경로는 하나다.
 */
const digitsOf = (s: string) => s.replace(/[^0-9]/g, '');
function photoScore(fileName: string, r: FineRow): number {
  const base = fileName.replace(/[.][^.]+$/, '');
  const flat = base.replace(/[ _().-]/g, '');
  const d = digitsOf(base);
  let score = 0;
  for (const no of [r.notice_no, r.giro_no]) {
    const nd = digitsOf(no ?? '');
    if (nd.length >= 6 && d.includes(nd)) score = Math.max(score, 100);
  }
  const car = r.car_no.replace(/ /g, '');
  const last4 = /([0-9]{4})$/.exec(car)?.[1];
  if (car && flat.includes(car)) score += 50;
  else if (last4 && (base.match(/[0-9]+/g) ?? [] as string[]).includes(last4)) score += 30;
  if (score === 0) return 0;
  const ymd = digitsOf(r.violation_date);
  if (ymd.length === 8 && d.includes(ymd)) score += 20;
  else if (ymd.length === 8 && d.includes(ymd.slice(4))) score += 10;
  return score;
}
/** 점수가 가장 높은 짝부터 확정(한 고지서엔 한 장) · 1등이 둘이면 비워 둔다. */
function autoPair(files: File[], pool: FineRow[]): (number | null)[] {
  const out: (number | null)[] = files.map(() => null);
  const pairs: { i: number; id: number; s: number }[] = [];
  files.forEach((f, i) => {
    // 사진이 이미 있는 고지서엔 자동으로 짝짓지 않는다 — 앞 사진을 덮지 않게(#412) · 필요하면 손으로 고른다
    const scored = pool.filter((r) => !r.has_photo).map((r) => ({ id: r.fine_id, s: photoScore(f.name, r) })).filter((x) => x.s >= 30).sort((a, b) => b.s - a.s);
    if (scored[0] && scored[1]?.s !== scored[0].s) pairs.push({ i, id: scored[0].id, s: scored[0].s });
  });
  const used = new Set<number>();
  for (const p of pairs.sort((a, b) => b.s - a.s)) {
    if (used.has(p.id)) continue;
    out[p.i] = p.id; used.add(p.id);
  }
  return out;
}

type PhotoItem = { file: File; url: string | null; fineId: number | null; state: 'wait' | 'up' | 'ok' | 'err'; error?: string };
function FinePhotoBulk({ pool, onClose, onDone }: { pool: FineRow[]; onClose: () => void; onDone: () => void }) {
  const [items, setItems] = useState<PhotoItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);
  // 미리보기 URL 은 파일을 다시 고르거나 창을 닫을 때만 푼다(상태 갱신마다 풀면 그림이 사라진다).
  const urls = useRef<string[]>([]);
  const freeUrls = () => { urls.current.forEach((u) => URL.revokeObjectURL(u)); urls.current = []; };
  useEffect(() => freeUrls, []);
  const label = (r: FineRow) => `${r.violation_date} · ${r.car_no} · ${r.kind} · ${money(r.amount)}원${r.driver_name ? ` · ${r.driver_name}` : ''}${r.has_photo ? ' · 사진 있음' : ''}`;

  function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    if (!files.length) return;
    const ids = autoPair(files, pool);
    freeUrls();
    setFinished(false);
    setItems(files.map((file, i) => {
      const url = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      if (url) urls.current.push(url);
      return { file, url, fineId: ids[i] ?? null, state: 'wait' };
    }));
  }
  const setId = (i: number, id: number | null) => setItems((xs) => xs.map((x, k) => (k === i ? { ...x, fineId: id } : x)));
  const dupIds = useMemo(() => {
    const seen = new Map<number, number>();
    items.forEach((x) => { if (x.fineId) seen.set(x.fineId, (seen.get(x.fineId) ?? 0) + 1); });
    return new Set([...seen].filter(([, n]) => n > 1).map(([id]) => id));
  }, [items]);
  const ready = items.filter((x) => x.fineId && x.state !== 'ok');
  const okN = items.filter((x) => x.state === 'ok').length;

  async function upload() {
    setBusy(true);
    for (const [i, it] of items.entries()) {
      if (!it.fineId || it.state === 'ok') continue;
      setItems((xs) => xs.map((x, k) => (k === i ? { ...x, state: 'up', error: undefined } : x)));
      try {
        await uploadFineImage(it.fineId, it.file);
        setItems((xs) => xs.map((x, k) => (k === i ? { ...x, state: 'ok' } : x)));
      } catch (e) { setItems((xs) => xs.map((x, k) => (k === i ? { ...x, state: 'err', error: errMsg(e) } : x))); }
    }
    setBusy(false); setFinished(true); onDone();
  }

  return (
    <div className="card space-y-3 p-5">
      <h3 className="text-lg font-semibold">고지서 사진 여러 장 올리기</h3>
      <p className="text-sm text-ink-soft">
        사진(JPG·PNG)이나 PDF 를 한꺼번에 고르시면 파일 이름에 든 <b>차량번호·위반일·고지서번호</b>로 고지서를 짝지어 둡니다.
        짝이 비었거나 다른 고지서면 오른쪽에서 고르시면 됩니다. 고를 수 있는 고지서는 지금 목록{pool.length > 0 ? ` ${pool.length.toLocaleString()}건` : ''}이고,
        목록에서 체크한 고지서가 있으면 그 안에서만 고릅니다. 이미 사진이 있는 고지서는 새 사진으로 바뀝니다.
      </p>
      {pool.length === 0 && <ErrorBox message="짝지을 고지서가 목록에 없습니다. 기간·구분을 바꿔 고지서가 보이게 한 뒤 다시 열어 주세요." />}
      <label className="btn-ghost inline-block cursor-pointer">
        {items.length ? '파일 다시 고르기' : '파일 고르기'}
        <input type="file" multiple accept="image/jpeg,image/png,application/pdf" className="hidden" disabled={busy || pool.length === 0} onChange={pick} />
      </label>
      {items.length > 0 && (
        <div className="max-h-[55vh] overflow-y-auto rounded border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 text-left text-xs text-ink-soft">
              <tr>
                <th className="bg-canvas px-2 py-2">사진</th><th className="bg-canvas px-2">파일</th>
                <th className="bg-canvas px-2">붙일 고지서</th><th className="bg-canvas px-2">결과</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b align-middle">
                  <td className="px-2 py-1.5">
                    {it.url ? <img src={it.url} alt="" className="h-14 w-14 rounded object-cover" /> : <span className="text-xs text-ink-faint">PDF</span>}
                  </td>
                  <td className="max-w-[14rem] truncate px-2" title={it.file.name}>{it.file.name}</td>
                  <td className="px-2">
                    <select className={`field w-full ${it.fineId && dupIds.has(it.fineId) ? 'border-red-400' : ''}`} value={it.fineId ?? ''}
                            disabled={busy || it.state === 'ok'} onChange={(e) => setId(i, e.target.value ? Number(e.target.value) : null)}>
                      <option value="">— 고르기 (안 고르면 건너뜀) —</option>
                      {pool.map((r) => <option key={r.fine_id} value={r.fine_id}>{label(r)}</option>)}
                    </select>
                    {it.fineId && dupIds.has(it.fineId) && <div className="text-xs text-red-600">같은 고지서를 고른 사진이 또 있습니다 — 나중에 올린 사진이 남습니다</div>}
                  </td>
                  <td className="whitespace-nowrap px-2 text-xs">
                    {it.state === 'up' ? '올리는 중…' : it.state === 'ok' ? <span className="text-emerald-700">붙였습니다</span>
                      : it.state === 'err' ? <span className="text-red-600" title={it.error}>실패 · {it.error}</span>
                      : it.fineId ? '' : <span className="text-ink-faint">건너뜀</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 border-t pt-3">
        <span className="text-sm text-ink-soft">
          {items.length > 0 && `${items.length}장 중 짝 ${items.filter((x) => x.fineId).length}장`}{okN > 0 && ` · 올림 ${okN}장`}
        </span>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>{finished ? '닫기' : '취소'}</button>
          <button className="btn-primary" onClick={() => void upload()} disabled={busy || ready.length === 0}>
            {busy ? '올리는 중…' : `${ready.length}장 올리기`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 그날 그 차의 배차 중에서 실제 기사를 고른다(영업담당자·회계 공용). */
function ConfirmPicker({ row, onDone, acct = false }: { row: FineRow; onDone: () => void; acct?: boolean }) {
  const [cands, setCands] = useState<FineCandidate[] | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const need = row.match_state === 'check';
  // 회계(#191)는 확인 필요가 아니어도 늘 바꿀 수 있다 — 제안 후보 + 「다른 편성 찾기」.
  const show = acct || need || row.driver_kind === '직영기사';
  useEffect(() => {
    if (!show) return;
    api.getFineCandidates({ fineId: row.fine_id }).then((r) => setCands(r.rows)).catch((e) => setError(errMsg(e)));
  }, [row.fine_id, show]);
  if (!show) return null;

  async function pick(carAllocId: number | null, skip = false) {
    setBusy(true); setError(null);
    try { await api.confirmFineDriver({ fineId: row.fine_id, carAllocId: carAllocId ?? undefined, skip, note: note || undefined }); onDone(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  const all = (cands ?? []).filter((c, i, a) => c.car_alloc_id && a.findIndex((x) => x.car_alloc_id === c.car_alloc_id) === i);
  const uniq = all.filter((c) => !c.acct);
  // 그 차의 회계계정 편성(#102) — 배차와 겹치지 않을 때만 따로 띄운다
  const accts = all.filter((c) => c.acct && !uniq.some((u) => u.car_alloc_id === c.car_alloc_id));
  return (
    <div className="mt-3 space-y-2">
      <div className="text-xs text-ink-soft">{row.violation_date} 이 차의 배차 — 실제로 운전한 기사를 고르세요.</div>
      {cands === null && !error && <div className="text-xs text-ink-soft">불러오는 중…</div>}
      {cands && uniq.length === 0 && <div className="text-xs text-ink-soft">그날 이 차의 배차가 없습니다. 편성 기사나 차량 회계로 확정하거나 비고에 사유를 적어 주세요.</div>}
      <div className="flex flex-wrap gap-2">
        {uniq.map((c) => (
          <button key={c.car_alloc_id} className="btn-ghost" disabled={busy} onClick={() => void pick(c.car_alloc_id)}
                  title={(cands ?? []).filter((x) => x.car_alloc_id === c.car_alloc_id).map((x) => `${dash(x.start_at)}~${dash(x.end_at)} ${dash(x.cust_name)} ${dash(x.dispt_name)}`).join('\n')}>
            {dash(c.driver_name)}{kindLabel(c.driver_kind) && <span className="ml-1 text-[10px] text-ink-faint">{kindLabel(c.driver_kind)}</span>}
            <span className="ml-1 text-xs text-ink-soft">{dash(c.cust_name)}</span>
          </button>
        ))}
        {row.car_alloc_id && !uniq.some((c) => c.car_alloc_id === row.car_alloc_id) && (
          <button className="btn-ghost" disabled={busy} onClick={() => void pick(row.car_alloc_id)}>편성 기사 {dash(row.driver_name)}로 확정</button>
        )}
        {accts.map((c) => (
          <button key={c.car_alloc_id} className="btn-ghost" disabled={busy} onClick={() => void pick(c.car_alloc_id)}
                  title={`기사 대신 이 차의 회계 편성(${dash(c.driver_name)} · 편성 ${c.car_alloc_id})으로 공제합니다. 기사 알림은 보내지 않습니다.`}>
            차량 회계로 확정<span className="ml-1 text-xs text-ink-soft">{dash(c.driver_name)}</span>
          </button>
        ))}
      </div>
      {acct && <AllocSearch row={row} busy={busy} onPick={(id) => void pick(id)} />}
      <div className="flex items-center gap-2">
        <input className="field flex-1" placeholder="메모(선택) — 예: 대차 운행, 기사 본인 확인" value={note} onChange={(e) => setNote(e.target.value)} />
        <button className="btn-ghost" disabled={busy} onClick={() => void pick(null, true)} title="여기서 못 정하면 회계팀으로 넘깁니다">모르겠음 · 회계로</button>
      </div>
      {error && <ErrorBox message={error} />}
    </div>
  );
}

/**
 * 다른 편성 찾기(#191 · 회계) — 검색어 없으면 이 차의 편성 전부(위반일에 걸친 것 먼저 · 회계계정·끝난 편성 포함),
 * 검색어가 있으면 차량번호·기사·차주·소속·편성번호로 전체에서. 누르면 그 편성으로 확정한다.
 */
function AllocSearch({ row, busy, onPick }: { row: FineRow; busy: boolean; onPick: (carAllocId: number) => void }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  useAutoSearch(term, (v) => setQ(v.trim()));
  const [hits, setHits] = useState<FineAllocHit[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    let off = false;
    setHits(null); setErr(null);
    api.searchFineAllocs({ fineId: row.fine_id, q: q || undefined })
      .then((r) => { if (!off) setHits(r.rows); }).catch((e) => { if (!off) setErr(errMsg(e)); });
    return () => { off = true; };
  }, [open, q, row.fine_id]);
  if (!open) {
    return <button type="button" className="btn-ghost text-xs" onClick={() => setOpen(true)}>다른 편성 찾기(회계계정·지난 편성 포함)</button>;
  }
  return (
    <div className="space-y-1.5 rounded border border-line p-2">
      <div className="flex items-center gap-2">
        <input className="field flex-1" autoFocus placeholder={`비우면 ${row.car_no} 의 편성 전부 · 기사·차주·차량번호·편성번호로 찾기`}
               value={term} onChange={(e) => setTerm(e.target.value)} />
        <button type="button" className="btn-ghost text-xs" onClick={() => setOpen(false)}>닫기</button>
      </div>
      {err && <ErrorBox message={err} />}
      {hits === null && !err && <div className="text-xs text-ink-soft">불러오는 중…</div>}
      {hits && hits.length === 0 && <div className="text-xs text-ink-soft">맞는 편성이 없습니다.</div>}
      {hits && hits.length > 0 && (
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-canvas text-left text-ink-soft">
              <tr>{['편성', '차량', '기사', '차주', '소속', '기간', ''].map((h) => <th key={h} className="px-1.5 py-1">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-line">
              {hits.map((h) => (
                <tr key={h.car_alloc_id} className={h.on_date ? 'bg-amber-50' : ''}>
                  <td className="px-1.5 py-1 font-mono">{h.car_alloc_id}</td>
                  <td className="px-1.5 py-1">{dash(h.car_no)}</td>
                  <td className="px-1.5 py-1">
                    {dash(h.driver_name)}
                    {h.acct ? <span className="ml-1 rounded bg-sky-50 px-1 text-[10px] text-sky-700">회계계정</span>
                      : kindLabel(h.driver_kind) && <span className="ml-1 text-[10px] text-ink-faint">{kindLabel(h.driver_kind)}</span>}
                  </td>
                  <td className="px-1.5 py-1">{dash(h.owner_name)}</td>
                  <td className="px-1.5 py-1">{dash(h.grp_name)}</td>
                  <td className="whitespace-nowrap px-1.5 py-1">
                    {h.startdt || '처음'} ~ {h.enddt || '무기한'}
                    {h.on_date && <span className="ml-1 font-semibold text-amber-800">위반일 운행</span>}
                    {h.is_use === false && <span className="ml-1 text-ink-faint">정산 끝</span>}
                  </td>
                  <td className="px-1.5 py-1 text-right">
                    <button type="button" className="btn-ghost px-2 py-0.5 text-xs" disabled={busy || h.car_alloc_id === row.car_alloc_id}
                            onClick={() => onPick(h.car_alloc_id)}>{h.car_alloc_id === row.car_alloc_id ? '지금 편성' : '이 편성으로'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 스캔함(10-01) — 드라이브 「과태료 관리」 폴더에 스캔을 올리면 AI 가 읽어 자동 등록·사진 첨부(functions/src/fineScan.ts).
// 사람이 볼 것은 「확인 필요」(판독이 모자람·같은 후보 여럿)와 「오류」뿐 — 사진 옆에 판독값을 채워 보여 주고 등록·연결·버림.
// ─────────────────────────────────────────────────────────────────────────────
const SCAN_STATE: Record<FineScanRow['state'], [string, string]> = {
  new: ['처리 중', 'bg-canvas text-ink-soft'], added: ['새로 등록', 'bg-emerald-50 text-emerald-700'], attached: ['사진만 붙임', 'bg-sky-50 text-sky-700'],
  ready: ['등록 대기', 'bg-sky-50 text-sky-800'], check: ['확인 필요', 'bg-amber-50 text-amber-800'], done: ['확인 끝', 'bg-emerald-50 text-emerald-700'], skip: ['버림', 'bg-canvas text-ink-faint'],
  error: ['오류', 'bg-red-50 text-red-700'],
};

interface ScanProgress {
  /** 전체 장 수 — 첫 응답 전엔 모른다(null) */
  total: number | null; done: number; added: number; attached: number; ready: number; check: number; error: number;
  log: FineScanRun['items']; running: boolean; stopped: boolean; failure: string | null;
}

/** 「지금 가져오기」 진행 모달 — 막대 · 몇/몇 장 · 결과별 개수 · 방금 읽은 파일. 실행 중엔 닫지 못하고 「멈추기」만(다음 묶음 전에 멈춘다). */
function ScanProgressModal(props: { p: ScanProgress; onStop: () => void; onClose: () => void }) {
  return (
    <Modal width="max-w-2xl">
      <ScanProgressCard {...props} />
    </Modal>
  );
}

function ScanProgressCard({ p, onStop, onClose }: { p: ScanProgress; onStop: () => void; onClose: () => void }) {
  const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
  const title = p.running ? (p.total === null ? '스캔함을 여는 중…' : `읽는 중 — ${p.done} / ${p.total}장`)
    : p.failure ? '중간에 멈췄습니다' : p.stopped ? `멈췄습니다 — ${p.done} / ${p.total ?? p.done}장` : `끝났습니다 — ${p.done}장`;
  return (
      <div className="card space-y-3 p-5 text-sm">
        <h2 className="font-bold">{title}</h2>
        <div className="h-3 overflow-hidden rounded-full bg-canvas">
          <div className={`h-full transition-all ${p.failure ? 'bg-red-500' : 'bg-brand-600'} ${p.running && p.total === null ? 'w-1/12 animate-pulse' : ''}`}
               style={p.total !== null ? { width: `${p.total === 0 ? 100 : pct}%` } : undefined} />
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          <span>{p.total === null ? '장 수 세는 중' : `${pct}%`}</span>
          <span className="text-emerald-700">새로 등록 {p.added}</span>
          <span className="text-sky-800">등록 대기 {p.ready}</span>
          <span className="text-sky-700">사진만 붙임 {p.attached}</span>
          <span className="text-amber-800">확인 필요 {p.check}</span>
          <span className="text-red-700">오류 {p.error}</span>
        </div>
        {p.running && <div className="text-xs text-ink-soft">한 장에 10~20초 걸립니다. 3장씩 읽고 이어서 읽습니다 — 이 창을 켜 두세요.</div>}
        {p.total === 0 && !p.running && !p.failure && <div className="text-ink-soft">스캔함에 새로 읽을 파일이 없습니다.</div>}
        {p.failure && <ErrorBox message={p.failure} />}
        {p.log.length > 0 && (
          <ul className="max-h-60 overflow-y-auto rounded border text-xs">
            {p.log.map((l, i) => {
              const [label, cls] = SCAN_STATE[l.state];
              return (
                <li key={`${l.file}-${i}`} className="flex gap-2 border-t px-2 py-1 first:border-t-0">
                  <span className={`shrink-0 rounded px-1.5 py-0.5 font-medium ${cls}`}>{label}</span>
                  <span className="shrink-0">{l.file}</span>
                  {l.note && <span className="truncate text-ink-soft" title={l.note}>{l.note}</span>}
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex justify-end gap-2">
          {p.running ? <button className="btn-ghost" onClick={onStop} disabled={p.stopped}>{p.stopped ? '멈추는 중…' : '멈추기'}</button>
            : <button className="btn-primary" onClick={onClose}>닫기</button>}
        </div>
      </div>
  );
}

function FineScanPanel({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { me } = useMe();
  const [all, setAll] = useState(false);
  const [data, setData] = useState<FineScanList | null>(null);
  const [open, setOpen] = useState<FineScanRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  async function load(a = all) {
    setError(null);
    try { const d = await api.listFineScans({ state: a ? 'all' : 'todo' }); setData(d); return d; }
    catch (e) {
      // 서버(스캔함 함수)가 아직 안 올라간 동안 — 호출이 not-found/internal 로 떨어진다
      const code = (e as { code?: string }).code ?? '';
      setError(code === 'functions/not-found' || code === 'functions/internal' ? '스캔함 서버를 준비하고 있습니다(관리자 설정 대기). 그동안은 엑셀 파일 올리기·붙여넣기·한 건 입력을 써 주세요.' : errMsg(e));
    }
  }
  useEffect(() => { void load(all); }, [all]); // eslint-disable-line react-hooks/exhaustive-deps
  // 「지금 가져오기」 — 3장씩 끊어 남은 게 0 이 될 때까지 부른다(장 수 제한 없음). 진행은 모달로.
  const [prog, setProg] = useState<ScanProgress | null>(null);
  const stopRef = useRef(false);
  async function runNow() {
    setError(null); setMsg(null); stopRef.current = false;
    // 이번 누름의 시작 시각(KST) — 서버가 이 뒤에 실패한 파일은 이번엔 다시 안 잡는다(같은 오류 재시도 방지)
    const since = new Date(Date.now() + 9 * 3600_000 - 5_000).toISOString().slice(0, 19).replace('T', ' ');
    let p: ScanProgress = { total: null, done: 0, added: 0, attached: 0, ready: 0, check: 0, error: 0, log: [], running: true, stopped: false, failure: null };
    setProg(p);
    try {
      for (let round = 0; round < 500; round++) {
        const r = await api.runFineScan({ max: 3, since });
        p = {
          ...p, stopped: stopRef.current, done: p.done + r.processed, total: p.done + r.processed + r.remaining,
          added: p.added + r.added, attached: p.attached + r.attached, ready: p.ready + (r.ready ?? 0), check: p.check + r.check, error: p.error + r.error,
          log: [...r.items, ...p.log],
        };
        setProg(p);
        // 남은 게 없거나 · 이번에 한 장도 못 읽었거나(다른 순회가 잡고 있음) · 사용자가 멈췄으면 끝
        if (!r.more || r.processed === 0 || stopRef.current) break;
      }
      p = { ...p, running: false, stopped: stopRef.current };
    } catch (e) {
      p = { ...p, running: false, failure: errMsg(e) };
    }
    setProg(p);
    onDone(); await load();
  }
  async function toggleAuto(on: boolean) {
    setError(null);
    try { await api.setFineScanAuto({ on }); await load(); } catch (e) { setError(errMsg(e)); }
  }
  // #303 등록 대기를 골라 한 번에 등록 — 하나씩 「확인·등록」 → 손대지 않고 「등록」 누른 것과 같다(읽은 값 그대로).
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const readyRows = (data?.rows ?? []).filter((r) => r.state === 'ready');
  useEffect(() => {
    const ids = new Set(readyRows.map((r) => r.scan_id));
    setPicked((p) => new Set([...p].filter((id) => ids.has(id))));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  async function bulkRegister() {
    const rows = readyRows.filter((r) => picked.has(r.scan_id));
    if (!rows.length || !window.confirm(`고른 ${rows.length}건을 읽은 값 그대로 등록할까요?`)) return;
    setError(null); setMsg(null);
    let ok = 0;
    const fails: string[] = [];
    for (const [i, r] of rows.entries()) {
      const x = r.extracted;
      setBusy(`등록하는 중… ${i + 1}/${rows.length}`);
      try {
        await api.resolveFineScan({
          scanId: r.scan_id, action: 'register',
          fields: {
            kind: x?.kind ?? '기타', carNo: x?.carNo ?? '', dueDate: x?.dueDate ?? '', issuer: x?.issuer ?? '', noticeNo: x?.noticeNo ?? '',
            lines: (x?.lines ?? []).map((l) => ({
              violationDate: l.violationDate ?? '', violationTime: l.violationTime ?? '', place: l.place ?? '', content: l.content ?? '', amount: l.amount ? String(l.amount) : '',
            })),
          },
        });
        ok++;
      } catch (e) { fails.push(`${r.file_name}: ${errMsg(e)}`); }
    }
    setBusy(null);
    setPicked(new Set());
    setMsg(`${ok}건 등록했습니다.`);
    if (fails.length) setError(`등록하지 못한 ${fails.length}건 — 「확인·등록」으로 하나씩 고쳐 넣어 주세요.\n${fails.join('\n')}`);
    onDone(); await load();
  }
  const cfg = data?.config;
  const queue = (data?.rows ?? []).filter((r) => r.state === 'ready' || r.state === 'check');
  if (open) {
    return <FineScanResolve key={open.scan_id} row={open} left={queue.length} onBack={() => { setOpen(null); void load(); }}
      onDone={async (retry) => {
        onDone();
        if (retry) { setOpen(null); void runNow(); return; }
        // 다음 대기 건(등록 대기 먼저, 그다음 확인 필요) — 없으면 목록
        const d = await load();
        const next = (d?.rows ?? []).filter((r) => r.scan_id !== open.scan_id).sort((a, b) => (a.state === 'ready' ? 0 : 1) - (b.state === 'ready' ? 0 : 1))
          .find((r) => r.state === 'ready' || r.state === 'check');
        setOpen(next ?? null);
      }} />;
  }
  return (
    <div className="card space-y-3 p-5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-bold">스캔함 — 드라이브에 올린 고지서 스캔</h2>
        <a className="text-xs text-brand-700 underline" href={`https://drive.google.com/drive/folders/${cfg?.folder_id ?? ''}`} target="_blank" rel="noreferrer">드라이브 폴더 열기</a>
        <button className="btn-primary ml-auto" onClick={() => void runNow()} disabled={!!busy || !!prog?.running}>지금 가져오기</button>
      </div>
      <p className="text-ink-soft">
        「과태료 관리」 드라이브 폴더에 스캔을 올리면 AI 가 차량번호·위반일시·금액을 읽어 아래 <b>등록 대기</b>에 올립니다 —
        <b>「확인·등록」</b>을 눌러 사진과 읽은 값을 보고 등록하세요(등록하면 다음 건이 바로 열립니다).
        엑셀·붙여넣기로 <b>이미 들어온 고지서</b>면 새로 넣지 않고 <b>사진만 자동으로 붙입니다</b>. 잘 안 읽힌 스캔은 <b>확인 필요</b>로 남습니다.
      </p>
      <div className="flex flex-wrap items-center gap-3 rounded bg-canvas px-3 py-2 text-xs">
        <span>자동 가져오기(10분마다): <b className={cfg?.auto_on ? 'text-emerald-700' : 'text-ink-soft'}>{cfg?.auto_on ? '켜짐' : '꺼짐'}</b></span>
        {me?.role === 'admin' && cfg && (
          <button className="btn-ghost text-xs" onClick={() => void toggleAuto(!cfg.auto_on)}>{cfg.auto_on ? '끄기' : '켜기'}</button>
        )}
        <span className="text-ink-soft">마지막: {cfg?.last_result ?? '아직 없음'}</span>
      </div>
      {busy && <div className="text-ink-soft">{busy}</div>}
      {prog && <ScanProgressModal p={prog} onStop={() => { stopRef.current = true; setProg({ ...prog, stopped: true }); }} onClose={() => setProg(null)} />}
      {msg && <div className="rounded bg-emerald-50 px-3 py-2">{msg}</div>}
      {error && <ErrorBox message={error} />}
      <div className="flex items-center gap-2 text-xs">
        <label className="flex items-center gap-1"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> 처리된 것까지 보기</label>
        <span className="text-ink-soft">확인 필요·오류 {data?.todo ?? 0}건</span>
        {readyRows.length > 0 && (
          <button className="btn-primary ml-auto text-xs" disabled={!!busy || picked.size === 0} onClick={() => void bulkRegister()}>
            고른 {picked.size}건 등록
          </button>
        )}
      </div>
      <div className="max-h-[55vh] overflow-auto rounded border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-canvas text-left text-ink-soft">
            <tr>
              <th className="w-6 px-2 py-1">
                {readyRows.length > 0 && (
                  <input type="checkbox" title="등록 대기 전부 고르기" disabled={!!busy}
                    checked={picked.size > 0 && picked.size === readyRows.length}
                    onChange={(e) => setPicked(e.target.checked ? new Set(readyRows.map((r) => r.scan_id)) : new Set())} />
                )}
              </th>
              <th className="px-2 py-1">파일</th><th className="px-2">올라온 때</th><th className="px-2">상태</th><th className="px-2">읽은 내용 / 사유</th><th className="px-2"></th></tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((r) => {
              const [label, cls] = SCAN_STATE[r.state];
              const l0 = r.extracted?.lines?.[0];
              return (
                <tr key={r.scan_id} className="border-t align-top">
                  <td className="px-2 py-1">
                    {r.state === 'ready' && (
                      <input type="checkbox" disabled={!!busy} checked={picked.has(r.scan_id)}
                        onChange={(e) => setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(r.scan_id); else n.delete(r.scan_id); return n; })} />
                    )}
                  </td>
                  <td className="px-2 py-1">{r.file_name}</td>
                  <td className="whitespace-nowrap px-2">{dash(r.drive_created?.slice(0, 16) ?? null)}</td>
                  <td className="px-2"><span className={`whitespace-nowrap rounded px-1.5 py-0.5 font-medium ${cls}`}>{label}</span></td>
                  <td className="px-2">
                    {r.fine_id
                      ? <span>고지서 #{r.fine_id} · {r.fine_car_no} · {r.fine_violation_date} · {money(r.fine_amount)}{(r.extracted?.fineIds?.length ?? 0) > 1 && ` 외 ${r.extracted!.fineIds!.length - 1}건`}</span>
                      : l0 && <span className="text-ink-soft">{r.extracted?.carNo ?? '차량?'} · {l0.violationDate ?? '날짜?'} · {l0.amount ? `${l0.amount.toLocaleString()}원` : '금액?'}</span>}
                    {r.note && <div className={r.state === 'check' || r.state === 'error' ? 'text-amber-800' : 'text-ink-faint'}>{r.note}</div>}
                  </td>
                  <td className="px-2 text-right">
                    {(r.state === 'ready' || r.state === 'check' || r.state === 'error') && <button className={r.state === 'ready' ? 'btn-primary text-xs' : 'btn-ghost text-xs'} onClick={() => setOpen(r)}>{r.state === 'ready' ? '확인·등록' : '확인'}</button>}
                  </td>
                </tr>
              );
            })}
            {data && data.rows.length === 0 && <tr><td colSpan={6} className="px-2 py-4 text-center text-ink-faint">{all ? '아직 처리한 스캔이 없습니다.' : '확인할 스캔이 없습니다.'}</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end"><button className="btn-ghost" onClick={onClose} disabled={!!busy}>닫기</button></div>
    </div>
  );
}

function FineScanResolve({ row, left, onBack, onDone }: { row: FineScanRow; left: number; onBack: () => void; onDone: (retry?: boolean) => void }) {
  // 확인 후 등록(10-01 way) — 사진 옆에 판독값을 채운 표. 고지서 머리(차량·기한·기관) + 위반(통행) 줄 여러 개를 한 번에 등록한다.
  const x = row.extracted;
  type Line = { violationDate: string; violationTime: string; place: string; content: string; amount: string };
  const toLine = (l?: FineScanLine): Line => ({
    violationDate: l?.violationDate ?? '', violationTime: l?.violationTime ?? '', place: l?.place ?? '', content: l?.content ?? '', amount: l?.amount ? String(l.amount) : '',
  });
  const [head, setHead] = useState({ kind: x?.kind ?? '기타', carNo: x?.carNo ?? '', dueDate: x?.dueDate ?? '', issuer: x?.issuer ?? '', noticeNo: x?.noticeNo ?? '' });
  const [lines, setLines] = useState<Line[]>(x?.lines?.length ? x.lines.map(toLine) : [toLine()]);
  const [linkId, setLinkId] = useState('');
  const [img, setImg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    if (row.has_photo) fetchFineScanImageUrl(row.scan_id).then((u) => { url = u; setImg(u); }).catch((e) => setError(errMsg(e)));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [row.scan_id, row.has_photo]);
  async function act(action: 'register' | 'link' | 'skip' | 'retry') {
    setBusy(true); setError(null);
    try {
      await api.resolveFineScan({
        scanId: row.scan_id, action,
        fields: action === 'register' ? { ...head, lines } : undefined,
        fineId: action === 'link' ? Number(linkId) : undefined,
      });
      onDone(action === 'retry'); // 다시 읽기 = 목록으로 돌아가 바로 읽기 시작(보관한 사진으로) · 그 밖 = 다음 대기 건
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  const setLine = (i: number, k: keyof Line, v: string) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const total = lines.reduce((s, l) => s + (Number(l.amount.replace(/[^0-9]/g, '')) || 0), 0);
  const headInput = (k: keyof typeof head, label: string, type = 'text') => (
    <label className="flex flex-col"><span className="text-xs text-ink-soft">{label}</span>
      <input type={type} className="field" value={head[k]} disabled={busy} onChange={(e) => setHead({ ...head, [k]: e.target.value })} /></label>
  );
  // 차주명(#169) — 차량번호·첫 줄 위반일이 바뀌면 다시 찾는다(치는 중엔 0.4초 기다림).
  const [owners, setOwners] = useState<string[] | null>(null);
  const ownerDate = lines[0]?.violationDate ?? '';
  useEffect(() => {
    let off = false;
    setOwners(null);
    const t = setTimeout(() => {
      api.lookupFineOwner({ carNo: head.carNo, date: ownerDate || undefined })
        .then((r) => { if (!off) setOwners(r.owners); }).catch(() => { if (!off) setOwners([]); });
    }, 400);
    return () => { off = true; clearTimeout(t); };
  }, [head.carNo, ownerDate]);
  const ready = row.state === 'ready';
  return (
    <div className="card space-y-3 p-5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-bold">{ready ? '등록 확인' : '스캔 확인'} — {row.file_name}</h2>
        {left > 1 && <span className="text-xs text-ink-soft">남은 {left - 1}건</span>}
        <button className="btn-ghost ml-auto" onClick={onBack} disabled={busy}>◀ 목록</button>
      </div>
      {row.note && <div className={`rounded px-3 py-2 ${ready ? 'bg-sky-50 text-sky-800' : 'bg-amber-50 text-amber-800'}`}>{row.note}</div>}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="max-h-[72vh] overflow-auto rounded border bg-canvas">
          {img ? <a href={img} target="_blank" rel="noreferrer"><img src={img} alt="고지서 스캔" className="w-full" /></a>
            : <div className="p-6 text-center text-ink-faint">{row.has_photo ? '사진을 불러오는 중…' : '보관된 사진이 없습니다 — 「다시 읽기」'}</div>}
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col"><span className="text-xs text-ink-soft">구분</span>
              <select className="field" value={head.kind} disabled={busy} onChange={(e) => setHead({ ...head, kind: e.target.value })}>
                {KINDS.map((k) => <option key={k}>{k}</option>)}
              </select></label>
            {headInput('carNo', '차량번호')}
            {headInput('dueDate', '납부기한', 'date')}
            {headInput('issuer', '발행기관')}
            {headInput('noticeNo', '고지번호')}
            <div className="flex flex-col"><span className="text-xs text-ink-soft">차주명</span>
              <div className="field select-all bg-canvas font-semibold" title="차량번호·위반일의 편성 차주">
                {owners === null ? '찾는 중…' : owners.length ? owners.join(', ') : '—'}</div></div>
          </div>
          <div className="max-h-[40vh] overflow-auto rounded border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-canvas text-left text-ink-soft">
                <tr><th className="px-1 py-1">위반일</th><th className="px-1">시각</th><th className="px-1">장소</th><th className="px-1">내용</th><th className="px-1 text-right">금액(원)</th><th /></tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-1 py-0.5"><input type="date" className="field w-32 py-0.5 text-xs" value={l.violationDate} disabled={busy} onChange={(e) => setLine(i, 'violationDate', e.target.value)} /></td>
                    <td className="px-1"><input className="field w-16 py-0.5 text-xs" value={l.violationTime} placeholder="HH:MM" disabled={busy} onChange={(e) => setLine(i, 'violationTime', e.target.value)} /></td>
                    <td className="px-1"><input className="field w-full min-w-24 py-0.5 text-xs" value={l.place} disabled={busy} onChange={(e) => setLine(i, 'place', e.target.value)} /></td>
                    <td className="px-1"><input className="field w-full min-w-24 py-0.5 text-xs" value={l.content} disabled={busy} onChange={(e) => setLine(i, 'content', e.target.value)} /></td>
                    <td className="px-1"><input className="field w-20 py-0.5 text-right text-xs" value={l.amount} disabled={busy} onChange={(e) => setLine(i, 'amount', e.target.value)} /></td>
                    <td className="px-1"><button className="text-red-600" title="이 줄 빼기" disabled={busy || lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button className="btn-ghost text-xs" disabled={busy} onClick={() => setLines([...lines, toLine()])}>+ 줄 추가</button>
            <span className="ml-auto">통행 {lines.length}줄 · 합계 <b>{total.toLocaleString()}원</b> — 고지서의 납부할 금액과 같은지 보세요{lines.length > 1 && ' (목록엔 고지서 한 줄로 들어갑니다)'}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary" disabled={busy} onClick={() => void act('register')}>{busy ? '처리 중…' : `등록${left > 1 ? ' → 다음' : ''}`}</button>
            <button className="btn-ghost" disabled={busy} onClick={() => void act('retry')} title="AI 가 보관한 사진으로 다시 읽습니다">다시 읽기</button>
            <button className="btn-ghost text-red-600" disabled={busy} onClick={() => void act('skip')} title="고지서가 아니거나 이미 처리한 스캔">버림</button>
          </div>
          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <label className="flex flex-col"><span className="text-xs text-ink-soft">이미 있는 고지서에 사진만 붙이기 — 고지서 번호(#)</span>
              <input className="field w-40" value={linkId} disabled={busy} onChange={(e) => setLinkId(e.target.value.replace(/[^0-9]/g, ''))} /></label>
            <button className="btn-ghost" disabled={busy || !linkId} onClick={() => void act('link')}>연결</button>
          </div>
          {error && <ErrorBox message={error} />}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 붙여넣기 입력(②단계) — 경찰청 · 세외수입 · 통행료. 옛 FNS 의 같은 이름 탭에 붙이던 그 표를 그대로.
// ─────────────────────────────────────────────────────────────────────────────
const PASTE_HELP: Record<FinePasteFormat, string> = {
  경찰청: '이파인 과태료 조회표 — 순번·위반일시·위반장소·위반내용·차량번호·계좌번호·납부기한·납부금액·관할관서·문의전화번호·과태료번호',
  세외수입: '위택스 세외수입 조회표 — 자치단체·세목·…·전자납부번호·납부기한·납부세액·과세대상·위반장소내용·위반일시내용·납세번호 (차량번호는 과세대상에서 찾습니다)',
  통행료: '통행료 표 — 순번·법인·구분·발행기관·차량번호·위반일자·위반시간·위반장소·위반내용·비고·납부기한·과태료(금액)',
};

function FinePaste({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [format, setFormat] = useState<FinePasteFormat | ''>('');
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<FinePasteResult | null>(null);
  const [result, setResult] = useState<FinePasteResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function check() {
    setBusy('확인 중…(기사 찾기까지 해 보고 되돌립니다)'); setError(null); setPreview(null); setResult(null);
    try { setPreview(await api.importFinePaste({ text, format: format || undefined })); } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }
  async function commit() {
    setBusy('넣는 중…'); setError(null);
    try { setResult(await api.importFinePaste({ text, format: format || undefined, commit: true })); setPreview(null); onDone(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }
  // 이파인(.xls)·위택스(.xlsx)에서 내려받은 조회표 파일을 그대로 올린다 — 첫 시트를 표 글자로 바꿔 붙여넣기 칸에 채운다.
  // 보이는 글자(raw:false) 그대로 옮긴다(「32,000원」·「2026-09-20 20:18:03」) — 붙여넣기와 같은 모양이라 서버 파서가 그대로 읽는다.
  // 칸 안 줄바꿈(이파인 계좌번호 6줄)은 한 칸이 여러 줄로 쪼개지지 않게 공백으로 편다.
  async function pickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null); setPreview(null); setResult(null);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: false });
      const ws = wb.Sheets[wb.SheetNames[0] ?? ''];
      if (!ws) { setError('파일에 시트가 없습니다.'); return; }
      const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '', raw: false, blankrows: false });
      const tsv = aoa.map((r) => r.map((c) => String(c ?? '').replace(/[\r\n\t]+/g, ' ').trim()).join('\t')).join('\n');
      if (!tsv.trim()) { setError('파일에 읽을 줄이 없습니다.'); return; }
      setText(tsv);
    } catch (err) { setError(`파일을 읽지 못했습니다 — ${errMsg(err)}`); }
  }
  const show = result ?? preview;
  return (
    <>
      <div className="card space-y-3 p-5 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-bold">고지서 붙여넣기 · 파일 올리기</h2>
          <label className={`btn-ghost ml-auto cursor-pointer ${busy || result ? 'pointer-events-none opacity-50' : ''}`}
                 title="이파인(경찰청) .xls · 위택스(세외수입) .xlsx 조회표 파일을 내려받은 그대로 올립니다">
            엑셀 파일 올리기
            <input type="file" accept=".xls,.xlsx,.csv" className="hidden" onChange={(e) => void pickFile(e)} />
          </label>
        </div>
        <p className="text-ink-soft">
          조회 화면의 표를 <b>칸 이름(머리줄)까지</b> 드래그해 복사한 뒤 아래에 붙여 넣으세요. 어느 표인지는 머리줄로 알아봅니다
          (머리줄 없이 붙이면 형식을 골라 주세요). <b>확인</b>을 누르면 넣기 전에 몇 건이 들어가고 기사가 몇 명 찾아지는지 먼저 보여 드립니다.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-soft">형식</span>
          {(['', '경찰청', '세외수입', '통행료'] as const).map((f) => (
            <button key={f || 'auto'} onClick={() => { setFormat(f); setPreview(null); }}
                    className={`rounded-lg px-3 py-1 ${format === f ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink-soft hover:bg-canvas'}`}>
              {f || '자동(머리줄로)'}
            </button>
          ))}
        </div>
        {format && <div className="text-xs text-ink-soft">{PASTE_HELP[format]}</div>}
        <textarea className="field h-48 w-full font-mono text-xs" value={text} disabled={!!busy || !!result}
                  onChange={(e) => { setText(e.target.value); setPreview(null); }} placeholder="여기에 붙여 넣기 (Ctrl+V)" />
        {busy && <div className="text-ink-soft">{busy}</div>}
        {error && <ErrorBox message={error} />}
        {show && (
          <div className={`space-y-2 rounded p-3 ${result ? 'bg-emerald-50' : 'bg-canvas'}`}>
            <div className="font-medium">{result ? '넣었습니다' : '미리보기(아직 안 넣었습니다)'} — {show.format ?? ''} 표 {show.total.toLocaleString()}건</div>
            <ul className="list-inside list-disc">
              <li>새로 {show.added.toLocaleString()} · 이미 있음 {((show.dupCount ?? 0) + show.same + show.filled).toLocaleString()}
                {show.deletedKept ? ` · 웹에서 지운 것 ${show.deletedKept}(되살리지 않음)` : ''}</li>
              <li>기사 찾기: 찾음 {show.match.ok.toLocaleString()} · 확인 필요 {show.match.check.toLocaleString()}(그중 관리기사A {show.match.a.toLocaleString()})
                {show.match.live === 'failed' && <b className="text-red-700"> · ⚠️ 버스인 실시간 편성을 못 읽었습니다</b>}</li>
              {show.skippedCount > 0 && <li className="text-amber-800">못 읽은 줄 {show.skippedCount}: {show.skipped.join(', ')}{show.skippedCount > show.skipped.length ? ' …' : ''}</li>}
            </ul>
            {(show.dups?.length ?? 0) > 0 && (
              <details><summary className="cursor-pointer text-ink-soft">이미 있는 {show.dupCount}건 보기(같은 차량·날짜·금액 — 넣지 않습니다)</summary>
                <ul className="mt-1 max-h-32 overflow-y-auto text-xs">{show.dups!.map((d) => <li key={d}>{d}</li>)}</ul>
              </details>
            )}
            {!result && show.sample.length > 0 && (
              <div className="max-h-64 overflow-auto rounded border bg-white">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-canvas text-left text-ink-soft">
                    <tr><th className="px-2 py-1">위반일시</th><th className="px-2">차량번호</th><th className="px-2">장소 / 내용</th><th className="px-2 text-right">금액</th><th className="px-2">납부기한</th><th className="px-2">발행기관</th><th className="px-2">고지번호</th></tr>
                  </thead>
                  <tbody>
                    {show.sample.map((s, i) => (
                      <tr key={i} className="border-t">
                        <td className="whitespace-nowrap px-2 py-1">{s.violationDate} {s.violationTime ?? ''}</td>
                        <td className="whitespace-nowrap px-2">{s.carNo}</td>
                        <td className="px-2">{[s.place, s.content].filter(Boolean).join(' / ') || '—'}</td>
                        <td className="px-2 text-right">{money(s.amount)}</td>
                        <td className="whitespace-nowrap px-2">{dash(s.dueDate)}</td>
                        <td className="px-2">{dash(s.issuer)}</td>
                        <td className="px-2">{dash(s.noticeNo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {show.total > show.sample.length && <div className="px-2 py-1 text-xs text-ink-faint">앞 {show.sample.length}건만 보여 드립니다.</div>}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} disabled={!!busy}>{result ? '닫기' : '취소'}</button>
          {!result && <button className="btn-ghost" onClick={() => void check()} disabled={!!busy || !text.trim()}>확인</button>}
          {preview && !result && <button className="btn-primary" onClick={() => void commit()} disabled={!!busy || preview.added === 0}>{preview.added.toLocaleString()}건 넣기</button>}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 알림톡 자동 발송(매일 17시 · 10-08 유상은) — 켜기 전에 다음 17시 대상 수를 먼저 보여 준다.
// ─────────────────────────────────────────────────────────────────────────────
function FineAutoNotify({ onClose }: { onClose: () => void }) {
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');
  const [p, setP] = useState<Awaited<ReturnType<typeof api.previewFineAutoNotify>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () => api.previewFineAutoNotify({}).then(setP).catch((e) => setError(errMsg(e)));
  useEffect(() => { void load(); }, []);
  async function toggle() {
    if (!p) return;
    setBusy(true); setError(null);
    try { await api.setFineAutoNotify({ enabled: !p.enabled }); await load(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  const skipped = Object.entries(p?.skipped ?? {});
  return (
    <div className="card space-y-3 p-5 text-sm">
      <h2 className="font-bold">알림톡 자동 발송 — 매일 17시</h2>
      <p className="text-ink-soft">
        매일 오후 5시에 <b>{p?.from ?? '2026-10-09'} 이후 웹에 등록한 고지서</b> 중 아직 알림톡을 안 보낸 것을 기사님께 보냅니다(받는 사람 규칙은 「알림톡」 버튼과 같습니다).
        기사 확인 필요·공제 안 함·휴대폰 번호 없는 고지서는 보내지 않고, 기사가 정해지면 그다음 17시에 갑니다.
        시트(FNS)에서 넘어온 고지서와 그 전에 등록한 고지서는 자동으로 보내지 않습니다 — 필요하면 골라서 「알림톡」으로 보내 주세요.
      </p>
      {error && <ErrorBox message={error} />}
      {!p && !error && <div className="text-ink-soft">불러오는 중…</div>}
      {p && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`rounded-full px-3 py-1 font-medium ${p.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-canvas text-ink-soft'}`}>
              {p.enabled ? '켜짐' : '꺼짐'}{p.by && <span className="ml-1 text-xs font-normal">({p.by.split('@')[0]})</span>}
            </span>
            {canWrite && (
              <button className={p.enabled ? 'btn-ghost' : 'btn-primary'} disabled={busy} onClick={() => void toggle()}>
                {busy ? '바꾸는 중…' : p.enabled ? '자동 발송 끄기' : '자동 발송 켜기'}
              </button>
            )}
            {!p.templateReady && <span className="text-red-700">템플릿을 아직 쓸 수 없습니다 — {p.templateStatus}</span>}
          </div>
          <div>다음 17시에 보낼 것: <b>{p.willSend}건</b>{p.waiting > p.max && <span className="text-ink-soft"> (대기 {p.waiting} — 하루 {p.max}건씩)</span>}
            {skipped.length > 0 && <span className="text-ink-soft"> · 못 보냄 {skipped.map(([k, n]) => `${k} ${n}`).join(' · ')}</span>}</div>
          {p.lastAt && <div className="text-ink-soft">마지막 실행 {p.lastAt} — {p.lastNote}</div>}
          {p.rows.length > 0 && (
            <div className="max-h-64 overflow-auto rounded border">
              <table className="w-full text-xs">
                <thead><tr className="bg-canvas text-left"><th className="px-2 py-1">위반일</th><th className="px-2">차량</th><th className="px-2 text-right">금액</th><th className="px-2">받는 분</th><th className="px-2">번호</th></tr></thead>
                <tbody>{p.rows.map((r) => (
                  <tr key={r.fineId} className="border-t"><td className="px-2 py-1">{r.violationDate}</td><td className="px-2">{r.carNo}</td><td className="px-2 text-right">{money(r.amount)}</td><td className="px-2">{r.to}</td><td className="px-2">{r.phone}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </>
      )}
      <div className="flex justify-end"><button className="btn-ghost" onClick={onClose}>닫기</button></div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 기사 알림톡(②단계) — 보내기 전에 「누구에게 몇 건」을 먼저 보여 준다.
// ─────────────────────────────────────────────────────────────────────────────
function FineNotify({ fineIds, onClose, onDone }: { fineIds: number[]; onClose: () => void; onDone: () => void }) {
  const [p, setP] = useState<FineNotifyPreview | null>(null);
  const [resend, setResend] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [res, setRes] = useState<Awaited<ReturnType<typeof api.sendFineNotify>> | null>(null);
  useEffect(() => { api.previewFineNotify({ fineIds }).then(setP).catch((e) => setError(errMsg(e))); }, [fineIds]);

  const ready = (p?.rows ?? []).filter((r) => r.to && (resend || r.sentBefore === 0));
  const blockedWhy = !p ? null : !p.templateReady ? `알림톡 템플릿(${p.templateCode})을 아직 쓸 수 없습니다 — ${p.templateStatus}` : !p.inHours ? '알림톡은 09시~21시에만 보냅니다' : null;
  async function send() {
    setBusy(true); setError(null);
    try { setRes(await api.sendFineNotify({ fineIds: ready.map((r) => r.fineId), resend })); onDone(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  return (
    <>
      <div className="card space-y-3 p-5 text-sm">
        <h2 className="font-bold">기사님께 알림톡</h2>
        <p className="text-ink-soft">
          관리기사B는 차량 소유자 번호로, 관리기사A는 운전한 기사님 본인 번호로 보냅니다(옛 과태료 문자와 같은 순서). 기사가 아직 정해지지 않은 고지서는 보내지 않습니다.
          기사님은 알림톡의 <b>「고지서 보기」</b>로 고지서 내용과 올린 사진을 봅니다.
        </p>
        {error && <ErrorBox message={error} />}
        {!p && !error && <div className="text-ink-soft">받는 사람 찾는 중…</div>}
        {blockedWhy && <div className="rounded bg-amber-50 px-3 py-2 text-amber-900">{blockedWhy}</div>}
        {p && !res && (
          <>
            <div className="rounded bg-canvas p-3">
              고른 {p.rows.length}건 중 <b>보낼 것 {ready.length}건</b>
              {p.rows.some((r) => r.sentBefore > 0) && (
                <label className="ml-3 inline-flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={resend} onChange={(e) => setResend(e.target.checked)} /> 이미 보낸 것도 다시 보내기
                </label>
              )}
            </div>
            <div className="max-h-72 overflow-auto rounded border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-canvas text-left text-ink-soft">
                  <tr><th className="px-2 py-1">위반일</th><th className="px-2">차량번호</th><th className="px-2 text-right">금액</th><th className="px-2">받는 사람</th><th className="px-2">번호</th><th className="px-2">비고</th></tr>
                </thead>
                <tbody>
                  {p.rows.map((r) => (
                    <tr key={r.fineId} className={`border-t ${r.to && (resend || r.sentBefore === 0) ? '' : 'text-ink-faint'}`}>
                      <td className="whitespace-nowrap px-2 py-1">{r.violationDate}</td>
                      <td className="whitespace-nowrap px-2">{r.carNo}</td>
                      <td className="px-2 text-right">{money(r.amount)}</td>
                      <td className="px-2">{r.to ? `${r.to.name}${r.to.role === 'owner' ? ' (차량 소유자)' : ''}` : '—'}</td>
                      <td className="whitespace-nowrap px-2">{r.to?.phone ?? '—'}</td>
                      <td className="px-2">{r.reason ?? (r.sentBefore > 0 ? `보냄 ${dash(r.lastSent)}` : '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {p.sample && (
              <details><summary className="cursor-pointer text-ink-soft">보낼 문구 보기(첫 건)</summary>
                <pre className="mt-1 whitespace-pre-wrap rounded bg-canvas p-3 text-xs">{p.sample}</pre>
              </details>
            )}
          </>
        )}
        {res && (
          <div className="rounded bg-emerald-50 p-3">
            보냄 <b>{res.sent}</b> · 실패 {res.failed} · 건너뜀 {res.skipped.length}
            {res.errors.length > 0 && <ul className="mt-1 text-xs text-red-700">{res.errors.map((e) => <li key={e}>{e}</li>)}</ul>}
            {res.skipped.length > 0 && <details className="mt-1"><summary className="cursor-pointer text-xs">건너뛴 것</summary><ul className="text-xs">{res.skipped.map((s) => <li key={s}>{s}</li>)}</ul></details>}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>{res ? '닫기' : '취소'}</button>
          {!res && <button className="btn-primary" onClick={() => void send()} disabled={busy || !p || !!blockedWhy || ready.length === 0}>
            {busy ? '보내는 중…' : `${ready.length}건 보내기`}
          </button>}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FNS 시트 가져오기(1회 적재 · 전환기) — Results 탭
// ─────────────────────────────────────────────────────────────────────────────
function FineImport({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [rows, setRows] = useState<BankCell[][] | null>(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<FineImportResult | null>(null);
  const [result, setResult] = useState<FineImportResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function pickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('파일 읽는 중…'); setError(null); setPreview(null); setResult(null);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: false });
      const ws = wb.Sheets['Results'];
      if (!ws) throw new Error('「Results」 탭이 없습니다. 「과태료/통행료고지 SYS(FNS)」를 엑셀(.xlsx)로 내려받은 파일인지 확인해 주세요.');
      const aoa = XLSX.utils.sheet_to_json<BankCell[]>(ws, { header: 1, defval: '', raw: true, blankrows: false }).map((r) => r.slice(0, 80));
      setRows(aoa); setFileName(file.name);
      setBusy('확인 중…(기사 찾기까지 해 보고 되돌립니다)');
      setPreview(await api.importFineSheet({ fileName: file.name, rows: aoa }));
    } catch (err) { setError(errMsg(err)); setRows(null); } finally { setBusy(null); e.target.value = ''; }
  }
  async function commit() {
    if (!rows) return;
    setBusy('넣는 중…'); setError(null);
    try { setResult(await api.importFineSheet({ fileName, rows, commit: true })); setPreview(null); onDone(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }
  const show = result ?? preview;
  return (
    <>
      <div className="card space-y-3 p-5 text-sm">
        <h2 className="font-bold">과태료 시트 가져오기</h2>
        <p className="text-ink-soft">
          「과태료/통행료고지 SYS(FNS)」를 <b>파일 → 다운로드 → Microsoft Excel</b>로 받아 올리세요. <b>Results</b> 탭만 읽습니다.
          여러 번 올려도 같은 고지서는 한 번만 들어가고, 웹에서 적은 납부일자·공제 표시는 덮지 않습니다(시트 값은 웹의 빈칸만 채웁니다).
        </p>
        <input type="file" accept=".xlsx" onChange={(e) => void pickFile(e)} disabled={!!busy} />
        {busy && <div className="text-ink-soft">{busy}</div>}
        {error && <ErrorBox message={error} />}
        {show && (
          <div className={`rounded p-3 ${result ? 'bg-emerald-50' : 'bg-canvas'}`}>
            <div className="font-medium">{result ? '넣었습니다' : '미리보기(아직 안 넣었습니다)'} — {show.fileName}</div>
            <ul className="mt-1 list-inside list-disc">
              <li>시트 {show.total.toLocaleString()}건 → 새로 {show.added.toLocaleString()} · 빈칸 채움 {show.filled.toLocaleString()} · 그대로 {show.same.toLocaleString()}{show.deletedKept ? ` · 웹에서 지운 것 ${show.deletedKept}(되살리지 않음)` : ''}</li>
              <li>기사 찾기: 찾음 {show.match.ok.toLocaleString()} · 확인 필요 {show.match.check.toLocaleString()}(그중 관리기사A {show.match.a.toLocaleString()})
                {show.match.live === 'failed' && <b className="text-red-700"> · ⚠️ 버스인 실시간 편성을 못 읽었습니다</b>}</li>
              {show.skippedCount > 0 && <li>건너뜀 {show.skippedCount}줄: {show.skipped.join(', ')}{show.skippedCount > show.skipped.length ? ' …' : ''}</li>}
            </ul>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} disabled={!!busy}>{result ? '닫기' : '취소'}</button>
          {preview && !result && <button className="btn-primary" onClick={() => void commit()} disabled={!!busy}>넣기</button>}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 공제 — 스위치가 꺼져 있으면 버스인 재무추가 「엑셀대량등록」 양식, 켜져 있으면(버스인웹 전환 뒤) 재무추가에 바로 공제
// ─────────────────────────────────────────────────────────────────────────────
function DeductPanel({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { me } = useMe();
  const [mon, setMon] = useState(kst().slice(0, 7));
  const [data, setData] = useState<FineDeductExport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marked, setMarked] = useState<string | null>(null);
  const [arm, setArm] = useState<'commit' | 'switch' | null>(null);

  const load = () => api.exportFineDeduct({ deductMon: mon }).then(setData).catch((e) => setError(errMsg(e)));
  useEffect(() => {
    setData(null); setMarked(null); setError(null); setArm(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mon]);

  async function download() {
    if (!data) return;
    const XLSX = await import('xlsx');
    // 1장 = 버스인 업로드 양식(편성번호·지급공제구분·계정·금액·비고·실행일자 — 0 = 공제) · 2장 = 사람이 대조할 표
    const up = XLSX.utils.aoa_to_sheet([
      ['편성번호', '지급공제구분', '계정', '금액', '비고', '실행일자'],
      ...data.rows.map((r) => [r.carAllocId, 0, r.account, Math.round(Number(r.amount)), r.comment, '']),
    ]);
    const chk = XLSX.utils.aoa_to_sheet([
      ['편성번호', '차량번호', '기사', '계정', '금액', '비고'],
      ...data.rows.map((r) => [r.carAllocId, r.carNo, r.driverName ?? '', r.account, Math.round(Number(r.amount)), r.comment]),
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, up, '업로드');
    XLSX.utils.book_append_sheet(wb, chk, '대조');
    XLSX.writeFile(wb, `과태료공제_${mon}.xlsx`);
  }
  async function markDone() {
    if (!data) return;
    setBusy(true); setError(null);
    try {
      const r = await api.setFineDeductState({ fineIds: data.rows.map((x) => x.fineId), state: 'done' });
      setMarked(`${r.updated}건을 공제 완료로 표시했습니다.`); onDone();
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  /** 바로 공제 — 두 번 눌러야 들어간다(금액이 기사 명세에 바로 잡힌다). */
  async function commit() {
    if (!data) return;
    if (arm !== 'commit') { setArm('commit'); return; }
    setArm(null); setBusy(true); setError(null);
    try {
      const r = await api.commitFineDeduct({ deductMon: mon, fineIds: data.rows.map((x) => x.fineId) });
      setMarked(`재무추가 ${r.deductMon} 에 ${r.created.toLocaleString()}건 · ${money(r.amount)}원을 공제로 넣었습니다.`); onDone();
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  async function toggleSwitch() {
    if (!data) return;
    if (arm !== 'switch') { setArm('switch'); return; }
    setArm(null); setBusy(true); setError(null);
    try { await api.setFineDirectDeduct({ open: !data.directDeduct }); await load(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  const sum = (data?.rows ?? []).reduce((a, r) => a + Math.round(Number(r.amount)), 0);
  const direct = data?.directDeduct === true;
  const empty = !data || data.rows.length === 0 || busy;
  return (
    <>
      <div className="card space-y-3 p-5 text-sm">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-bold">{direct ? '재무추가에 바로 공제' : '버스인 공제 엑셀'}</h2>
          {me?.role === 'admin' && data && (
            <button className={`btn text-xs ${arm === 'switch' ? 'border-red-400 text-red-700' : ''}`} disabled={busy}
              onClick={() => void toggleSwitch()} onBlur={() => setArm(null)}
              title="버스인웹 전환(전량 재적재) 뒤에 켭니다 — 그 전에 넣으면 재적재가 재무추가 줄을 지웁니다">
              {arm === 'switch' ? (direct ? '정말 끌까요? 한 번 더' : '정말 켤까요? 한 번 더') : `바로 공제: ${direct ? '켜짐' : '꺼짐'}`}
            </button>
          )}
        </div>
        <p className="text-ink-soft">
          {direct ? (
            <>납부일자가 들어간 고지서 중 <b>공제 정산월</b>이 이 달인 것을 버스인웹 <b>재무추가</b>에 공제(계정: 과태료·통행료)로 바로 넣습니다.
              재무추가 대량등록과 같은 검사(편성·계정·마감)를 거치고, 한 건이라도 걸리면 아무것도 넣지 않습니다. 확인 필요 줄은 빠집니다.</>
          ) : (
            <>납부일자가 들어간 고지서 중 <b>공제 정산월</b>이 이 달인 것을 버스인 재무추가 「엑셀대량등록」 양식으로 만듭니다(계정: 과태료·통행료, 공제).
              버스인에 올린 뒤 <b>「공제 완료로 표시」</b>를 누르세요. 확인 필요 줄은 빠집니다.</>
          )}
        </p>
        <label className="flex items-center gap-2"><span>공제 정산월</span><input type="month" className="field" value={mon} onChange={(e) => setMon(e.target.value)} /></label>
        {error && <ErrorBox message={error} />}
        {!data && !error && <div className="text-ink-soft">불러오는 중…</div>}
        {data && (
          <>
            <div className="rounded bg-canvas p-3">
              공제할 것 <b>{data.rows.length.toLocaleString()}건 · {sum.toLocaleString()}원</b>
              {data.held.length > 0 && <> · <span className="text-amber-800">빠진 것 {data.held.length}건(확인 필요)</span></>}
              {marked !== null && <div className="mt-1 text-emerald-700">{marked}</div>}
            </div>
            {data.held.length > 0 && (
              <details><summary className="cursor-pointer text-amber-800">빠진 {data.held.length}건 보기</summary>
                <ul className="mt-1 max-h-48 overflow-y-auto text-xs">
                  {data.held.map((h) => <li key={h.fineId}>{h.violationDate} {h.carNo} {money(h.amount)}원 — {h.reason}</li>)}
                </ul>
              </details>
            )}
          </>
        )}
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>닫기</button>
          <button className="btn-ghost" onClick={() => void download()} disabled={empty}>{direct ? '대조표 내려받기' : '엑셀 내려받기'}</button>
          {direct ? (
            <button className={`btn-primary ${arm === 'commit' ? 'ring-2 ring-red-400' : ''}`} onClick={() => void commit()} onBlur={() => setArm(null)} disabled={empty}>
              {arm === 'commit' ? `${data?.rows.length.toLocaleString()}건 넣을까요? 한 번 더` : '재무추가에 공제 넣기'}
            </button>
          ) : (
            <button className="btn-primary" onClick={() => void markDone()} disabled={empty}>공제 완료로 표시</button>
          )}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 영업담당자 — 내게 온 확인 요청(대시보드 맨 위). 없으면 아무것도 안 그린다.
// ─────────────────────────────────────────────────────────────────────────────
export function MyFineRequests() {
  const [rows, setRows] = useState<FineRow[] | null>(null);
  const [open, setOpen] = useState<FineRow | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => { api.listMyFineRequests({}).then((r) => setRows(r.rows)).catch(() => setRows([])); }, [nonce]);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="card border-amber-200 bg-amber-50/60 p-4">
      <div className="font-semibold text-amber-900">과태료 기사 확인 요청 {rows.length}건</div>
      <p className="mt-1 text-sm text-amber-900/80">
        관리기사A 차량의 과태료입니다. 위반일에 이 차를 실제로 운전한 기사를 골라 주세요 — 고른 기사에게 공제됩니다.
      </p>
      <ul className="mt-2 divide-y rounded bg-white text-sm">
        {rows.map((r) => (
          <li key={r.fine_id}>
            <button className="flex w-full flex-wrap items-center gap-x-3 px-3 py-2 text-left hover:bg-canvas" onClick={() => setOpen(r)}>
              <span className="whitespace-nowrap">{r.violation_date} {r.violation_time ?? ''}</span>
              <span className="font-medium">{r.car_no}</span>
              <span className="text-ink-soft">{[r.place, r.content].filter(Boolean).join(' / ')}</span>
              <span className="ml-auto">{money(r.amount)}원</span>
            </button>
          </li>
        ))}
      </ul>
      {open && (
        <Modal>
          <FineConfirmCard row={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); setNonce((n) => n + 1); }} />
        </Modal>
      )}
    </div>
  );
}

function FineConfirmCard({ row: open, onClose, onDone }: { row: FineRow; onClose: () => void; onDone: () => void }) {
  return (
          <div className="card space-y-3 p-5 text-sm">
            <h2 className="font-bold">과태료 기사 확인</h2>
            <div>{open.violation_date} {open.violation_time ?? ''} · <b>{open.car_no}</b> · {open.kind} {money(open.amount)}원</div>
            <div className="text-ink-soft">{[open.place, open.content].filter(Boolean).join(' / ')}</div>
            <div className="text-ink-soft">편성 기사: {dash(open.driver_name)} (관리기사A)</div>
            {(open.has_photo || open.photo_url) && <div><PhotoLink r={open} label="고지서 보기" /></div>}
            <ConfirmPicker row={open} onDone={onDone} />
            <div className="flex justify-end"><button className="btn-ghost" onClick={onClose}>닫기</button></div>
          </div>
  );
}

/**
 * 「이미 공제함」(#221) — 출납일보(직납통행료)·재무추가에서 먼저 공제한 고지서를 그 공제 줄에 잇는다.
 * 같은 차·기사·금액 후보가 있을 때만 보인다. 이으면 공제 완료가 되고 바로 공제가 한 번 더 넣지 않는다.
 */
function AlreadyDeducted({ row, onDone }: { row: FineRow; onDone: () => void }) {
  const [rows, setRows] = useState<FineLinkCandidate[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let off = false;
    api.listFineLinkCandidates({ fineId: row.fine_id }).then((r) => !off && setRows(r.rows)).catch(() => !off && setRows([]));
    return () => { off = true; };
  }, [row.fine_id]);
  if (!rows || rows.length === 0) return null;
  async function link(id: number) {
    setBusy(true); setError(null);
    try { await api.linkFineDeduct({ fineId: row.fine_id, resultDailyId: id }); onDone(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  return (
    <div className="rounded border border-sky-200 bg-sky-50 p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>같은 금액의 재무추가 공제 줄이 {rows.length}건 있습니다. 출납일보 등에서 이미 공제하셨다면 그 줄과 연결하세요.</span>
        <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setOpen((v) => !v)}>{open ? '접기' : '이미 공제함 — 줄 고르기'}</button>
      </div>
      {open && (
        <table className="mt-2 w-full bg-white text-xs">
          <thead><tr><th className="th">정산월</th><th className="th">항목</th><th className="th text-right">금액</th><th className="th">기사 · 차량</th><th className="th">적요</th><th className="th">입력</th><th className="th" /></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.result_daily_id}>
                <td className="td">{c.drive_mon}</td>
                <td className="td">{dash(c.account_name)}</td>
                <td className="td num">{money(c.amount)}</td>
                <td className="td">{dash(c.driver_name)} · {dash(c.car_no)}</td>
                <td className="td max-w-[16rem] truncate" title={c.comment ?? ''}>{dash(c.comment)}</td>
                <td className="td">{dash(c.create_method)}</td>
                <td className="td text-right"><button className="btn-primary px-2 py-0.5 text-xs" disabled={busy} onClick={() => void link(c.result_daily_id)}>이 줄과 연결</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {error && <div className="mt-2"><ErrorBox message={error} /></div>}
    </div>
  );
}
